import * as THREE from 'three';
import { clamp, damp, DEG2RAD, lerp } from '../core/math';
import type { Input } from '../core/Input';
import type { PlayerController } from './PlayerController';
import type { AudioManager } from '../audio/AudioManager';
import {
  START_OWNED_WEAPONS,
  WEAPON_ORDER,
  WEAPON_SLOTS,
  WEAPONS,
  GL_BARREL_LOFT,
  isLauncherWeapon,
  isMeleeWeapon,
  isShotgunLike,
  pickSlotWeapon,
  type WeaponDef,
  type WeaponId,
} from '../weapons/WeaponDefs';
import {
  applyStance,
  bakedPunchClip,
  blendStance,
  clonePunchClips,
  copyPunchKey,
  PUNCH_COMBO_WINDOW,
  nextPunchCombo,
  punchDamage,
  punchImpactT,
  punchKeyOf,
  punchPlayDuration,
  punchStun,
  punchSwingRate,
  samplePunchClip,
  type PunchClip,
  type PunchClipId,
  type PunchKey,
  type PunchKeyId,
} from './PunchClip';
import type { ProjectileSystem } from '../weapons/Projectiles';
import { burstToilet, type ToiletSpraySystem } from '../world/ToiletSpray';
import { hitSurfaceFromObject } from '../world/HitSurface';
import type { ShotLineKind, ShotLineSystem } from '../world/ShotLines';
import { type LoadedViewWeapon } from './ViewmodelAssets';
import { ProcArms } from './ProcArms';
import { buildAllProcWeapons, makeAk47Mag, makeDoubleReloadPair, makeMp5Mag, makePistolMag, makePumpCasing, readM9GripShape } from './ProcWeapons';
import {
  bakedReloadClip,
  cloneReloadClip,
  copyReloadBeat,
  emptyReloadSample,
  sampleReloadClip,
  type DoubleReloadBeat,
  type DoubleReloadBeatId,
} from './DoubleReloadClip';
import {
  bakedMagReloadClip,
  cloneMagReloadClip,
  copyPistolReloadBeat,
  emptyPistolReloadSample,
  isMagReloadWeapon,
  samplePistolReloadClip,
  type MagReloadWeaponId,
  type PistolReloadBeat,
  type PistolReloadBeatId,
} from './PistolReloadClip';

export interface ShotEvent {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  weapon: WeaponDef;
  pelletIndex: number;
  silenced?: boolean;
}

type AnimState = 'idle' | 'firing' | 'reloading' | 'pumping' | 'switching';

const _gunCamM = new THREE.Matrix4();
const _gunCamS = new THREE.Vector3();

function holdTreesMatch(src: THREE.Object3D, dst: THREE.Object3D): boolean {
  if (src.children.length !== dst.children.length) return false;
  for (let i = 0; i < src.children.length; i++) {
    if (!holdTreesMatch(src.children[i]!, dst.children[i]!)) return false;
  }
  return true;
}

function copyHoldTree(src: THREE.Object3D, dst: THREE.Object3D): void {
  dst.position.copy(src.position);
  dst.quaternion.copy(src.quaternion);
  dst.scale.copy(src.scale);
  dst.visible = src.visible;
  const n = Math.min(src.children.length, dst.children.length);
  for (let i = 0; i < n; i++) copyHoldTree(src.children[i]!, dst.children[i]!);
}

/**
 * First-person arms + weapons. Procedural only — fused-hand GLBs are not loaded.
 */
export class Viewmodel {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;

  private readonly root = new THREE.Group();
  private readonly sway = new THREE.Group();
  private readonly bob = new THREE.Group();
  private readonly pose = new THREE.Group();
  private readonly weaponSlot = new THREE.Group();

  private pistol: LoadedViewWeapon | null = null;
  private shotgun: LoadedViewWeapon | null = null;
  private sawedoff: LoadedViewWeapon | null = null;
  private mp5: LoadedViewWeapon | null = null;
  private ak47: LoadedViewWeapon | null = null;
  private punch: LoadedViewWeapon | null = null;
  private gl: LoadedViewWeapon | null = null;
  private rpg: LoadedViewWeapon | null = null;
  private ready = false;
  private readonly owned = new Set<WeaponId>(START_OWNED_WEAPONS);
  private readonly ammo: Record<WeaponId, { mag: number; reserve: number }> = {
    pistol: { mag: WEAPONS.pistol.magSize, reserve: WEAPONS.pistol.reserveStart },
    shotgun: { mag: 0, reserve: 0 },
    sawedoff: { mag: 0, reserve: 0 },
    mp5: { mag: 0, reserve: 0 },
    ak47: { mag: 0, reserve: 0 },
    punch: { mag: 0, reserve: 0 },
    gl: { mag: 0, reserve: 0 },
    rpg: { mag: 0, reserve: 0 },
  };

  private readonly muzzleFlashGroup = new THREE.Group();
  private readonly muzzleFlashCore: THREE.Mesh;
  private readonly muzzleFlashSpike: THREE.Mesh;
  private readonly muzzleFlashSprite: THREE.Mesh;
  private readonly muzzleLight: THREE.PointLight;
  private readonly worldMuzzleLight: THREE.PointLight;
  private worldGlowT = 0;
  private worldGlowMaxT = 0.14;
  private worldGlowPeak = 0;
  private readonly smokePuffs: THREE.Sprite[] = [];
  private smokeMaps: THREE.Texture[] = [];
  private readonly impactSmoke: THREE.Sprite[] = [];
  private readonly impactDebris: THREE.Mesh[] = [];
  private readonly liveDecals: THREE.Mesh[] = [];
  private readonly decalPool: THREE.Mesh[] = [];
  private readonly sparkPool: THREE.Mesh[] = [];
  private readonly chipPool: THREE.Mesh[] = [];
  private shotLines: ShotLineSystem | null = null;
  private readonly debrisGeo = new THREE.BoxGeometry(0.02, 0.012, 0.016);
  private readonly sparkGeo = new THREE.SphereGeometry(0.012, 5, 5);
  private readonly decalGeo = new THREE.CircleGeometry(0.045, 10);
  private readonly decalLook = new THREE.Vector3();
  private readonly decalMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.88,
    side: THREE.DoubleSide,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  private readonly sparkMat = new THREE.MeshBasicMaterial({
    color: 0xffcc66,
    transparent: true,
    opacity: 0.95,
  });
  private readonly chipMat = new THREE.MeshBasicMaterial({
    color: 0x6a6558,
    transparent: true,
    opacity: 0.9,
  });
  private readonly casingGeo = new THREE.CylinderGeometry(0.014, 0.014, 0.042, 8);
  private readonly casingMat = new THREE.MeshStandardMaterial({
    color: 0xe8c84a,
    metalness: 0.95,
    roughness: 0.28,
    emissive: 0x3a2808,
    emissiveIntensity: 0.2,
  });
  private smokeSpriteMat: THREE.SpriteMaterial | null = null;
  private readonly casings: THREE.Group;
  private readonly worldCasings: THREE.Object3D[] = [];
  private worldScene: THREE.Object3D | null = null;
  private playerCamera: THREE.Camera | null = null;
  private groundY = 0;
  private readonly placeholder: THREE.Group;

  /** Procedural left arm for sprint run cycle (GLB hands are fused to the gun). */
  private readonly sprintArm = new THREE.Group();
  private readonly sprintShoulder = new THREE.Group();
  private readonly sprintElbow = new THREE.Group();
  private readonly sprintWrist = new THREE.Group();
  private sprintArmBlend = 0;
  private sprintRunPhase = 0;
  private readonly procMode = true;
  private procArms: ProcArms | null = null;

  /** First-person legs shown while sliding/gliding. */
  private readonly slideLegs = new THREE.Group();
  private readonly slideLegL = new THREE.Group();
  private readonly slideLegR = new THREE.Group();
  private readonly slideKneeL = new THREE.Group();
  private readonly slideKneeR = new THREE.Group();
  private slideLegsGlb: THREE.Group | null = null;
  private useGlbLegs = false;
  private slideLegBlend = 0;

  private weaponId: WeaponId = 'pistol';
  private pistolSilenced = true;
  private pendingToast: string | null = null;

  private ads = 0;
  /** When set, updatePose uses this ADS value instead of input (screenshot tuning). */
  private debugAdsLock: number | null = null;
  /** Studio posing: no bob/sway/recoil, snap hip/ADS instantly. */
  private calibFrozen = false;
  private calibAllowFire = true;
  private recoil = 0;
  private kickPitch = 0;
  private kickYaw = 0;
  private flashT = 0;
  private fireCooldown = 0;
  private autoShotFlip = false;
  private animT = 0;
  private animState: AnimState = 'idle';
  private pumpAmount = 0;
  private sprintBlend = 0;
  private breathPhase = 0;
  /** Brief window after sprint-cancel where gun snaps to hip for a shot. */
  private sprintFireRaiseT = 0;
  /** Weapon being drawn; visible gun stays on weaponId until swap. */
  private switchTarget: WeaponId | null = null;
  private pendingWeapon: WeaponId | null = null;
  private switchSwapped = false;
  private readonly switchDownDur = 0.08;
  private readonly switchUpDur = 0.1;
  /** Drop past the viewmodel frustum so the old gun leaves before the next rises. */
  private readonly switchDrop = 1.28;

  /** Break-reload: skip mag/reserve fill so the clip can loop in studio / HUD. */
  private reloadPreview = false;
  private reloadCalibHold = false;
  private reloadCalibBeat: DoubleReloadBeatId | null = null;
  private magCalibBeat: PistolReloadBeatId | null = null;
  private readonly reloadClip = cloneReloadClip();
  private readonly reloadSample = emptyReloadSample();
  private readonly magClips: Record<MagReloadWeaponId, PistolReloadBeat[]> = {
    pistol: cloneMagReloadClip('pistol'),
    mp5: cloneMagReloadClip('mp5'),
    ak47: cloneMagReloadClip('ak47'),
  };
  private readonly magSample = emptyPistolReloadSample();
  private pendingSawedReload = false;
  private pendingRpgReload = false;
  private rpgLoadClick = false;
  private punchStrike: PunchClipId | null = null;
  private punchT = 0;
  private punchComboStep = 0;
  private punchComboWindow = 0;
  private punchTiredT = 0;
  /** Fists stay up this long after a punch or while aiming, then drop like a sprint. */
  private punchHotT = 0;
  private punchFrozenClip: PunchClipId | null = null;
  private punchFrozenKey: PunchKeyId | null = null;
  private punchPendingHit = false;
  private punchHitPlayer: PlayerController | null = null;
  private readonly punchClips = clonePunchClips();
  private projectiles: ProjectileSystem | null = null;
  private doubleReloadEjected = false;
  private doubleReloadInserted = false;
  private doubleReloadCloseSfx = false;
  private magDropped = false;
  private magInserted = false;
  private magSeatSfx = false;
  private readonly reloadFlyers: THREE.Object3D[] = [];
  private reloadPair: THREE.Group | null = null;
  private magHeld: THREE.Group | null = null;
  private magHeldKind: MagReloadWeaponId | null = null;
  private readonly reloadHandTarget = new THREE.Object3D();
  private readonly reloadTmpV = new THREE.Vector3();
  private readonly reloadTmpV2 = new THREE.Vector3();
  private readonly reloadTmpQ = new THREE.Quaternion();
  private readonly reloadTmpQ2 = new THREE.Quaternion();

  /**
   * CS2-style viewmodel: dead-center X, parked in the bottom ~30% of the frame,
   * looking slightly over the slide. ADS lifts toward the crosshair.
   */
  private readonly hipPos = new THREE.Vector3(0.055, -0.11, -0.34);
  private readonly hipRot = new THREE.Euler(0.08, 0.09, 0.0);
  private readonly adsPos = new THREE.Vector3(0.015, -0.095, -0.26);
  private readonly adsRot = new THREE.Euler(0.0, 0.0, 0.0);
  /**
   * Shotgun hip keeps the angled mesh hold; ADS cancels mesh yaw (+0.32) so the
   * stock/barrel run straight down the sights. Both shifted right on screen.
   */
  private readonly shotgunHipPos = new THREE.Vector3(0.11, -0.085, -0.3);
  private readonly shotgunHipRot = new THREE.Euler(0.1, 0.0, 0.0); // tip muzzle up → stock a bit lower
  private readonly shotgunAdsPos = new THREE.Vector3(0.03, -0.045, -0.24);
  private readonly shotgunAdsRot = new THREE.Euler(-0.02, -0.22, 0.0);
  /** SMG / rifle: studio-tuned AK-47 hold. */
  private readonly rifleHipPos = new THREE.Vector3(0.009, -0.023, -0.343);
  private readonly rifleHipRot = new THREE.Euler(0.09, -0.305, -0.13);
  private readonly rifleAdsPos = new THREE.Vector3(-0.017, 0.002, -0.297);
  private readonly rifleAdsRot = new THREE.Euler(-0.08, -0.43, -0.155);
  /** MP5: studio-tuned hip; ADS stays on the iron-sight hold. */
  private readonly mp5HipPos = new THREE.Vector3(0.029, -0.094, -0.297);
  private readonly mp5HipRot = new THREE.Euler(0.195, 0.19, -0.105);
  private readonly mp5AdsPos = new THREE.Vector3(0.0, -0.07, -0.22);
  private readonly mp5AdsRot = new THREE.Euler(0.08, 0.1, -0.05);
  /** Procedural guns already face -Z; simpler hip / ADS than the fused GLBs. */
  private readonly procHipPos = new THREE.Vector3(0.065, -0.125, -0.28);
  private readonly procHipRot = new THREE.Euler(0.08, 0.05, 0.02);
  private readonly procAdsPos = new THREE.Vector3(0.0, -0.042, -0.22);
  private readonly procAdsRot = new THREE.Euler(0.0, 0.0, 0.0);
  /**
   * Pistol framing ≈ hand-studio mouse-wheel zoom (exp 0.12 / tick from 0.28m).
   * Hip = 5 ticks out, ADS = 4 ticks out.
   */
  private readonly procPistolHipPos = new THREE.Vector3(0.065, -0.125, -0.51);
  private readonly procPistolHipRot = new THREE.Euler(0.08, 0.05, 0.02);
  private readonly procPistolAdsPos = new THREE.Vector3(0.0, -0.042, -0.393);
  private readonly procPistolAdsRot = new THREE.Euler(0.0, 0.0, 0.0);
  private readonly procSgHipPos = new THREE.Vector3(0.085, -0.135, -0.43);
  private readonly procSgHipRot = new THREE.Euler(0.1, 0.04, 0.02);
  private readonly procSgAdsPos = new THREE.Vector3(0.0, -0.04, -0.24);
  private readonly procSgAdsRot = new THREE.Euler(-0.02, 0.0, 0.0);
  private readonly procRifleHipPos = new THREE.Vector3(0.055, -0.115, -0.3);
  private readonly procRifleHipRot = new THREE.Euler(0.06, 0.03, -0.02);
  private readonly procRifleAdsRot = new THREE.Euler(0.0, 0.0, 0.0);
  private readonly procAk47AdsPos = new THREE.Vector3(0.0, -0.05, -0.22);
  private readonly procMp5AdsPos = new THREE.Vector3(0.0, -0.058, -0.22);
  private readonly procMp5AdsRot = new THREE.Euler(0.0, 0.0, 0.0);
  private readonly procSawedHipPos = new THREE.Vector3(0.07, -0.11, -0.42);
  private readonly procSawedHipRot = new THREE.Euler(0.08, 0.03, 0.01);
  private readonly procSawedAdsPos = new THREE.Vector3(0.0, -0.042, -0.28);
  private readonly procSawedAdsRot = new THREE.Euler(-0.03, 0.0, 0.0);
  private readonly punchHipPos = new THREE.Vector3(0, -0.02, 0);
  private readonly punchHipRot = new THREE.Euler(0, 0, 0);
  private readonly punchAdsPos = new THREE.Vector3(0, 0, 0);
  private readonly punchAdsRot = new THREE.Euler(0, 0, 0);
  private readonly glHipPos = new THREE.Vector3(0.065, -0.125, -0.28);
  private readonly glHipRot = new THREE.Euler(0.08, 0.05, 0.02);
  private readonly glAdsPos = new THREE.Vector3(0.0, -0.072, -0.24);
  private readonly glAdsRot = new THREE.Euler(0.04, 0.0, 0.0);
  /** Hip pulled back 3 studio wheel ticks (exp 0.12) from the default proc hold. */
  private readonly rpgHipPos = new THREE.Vector3(0.065, -0.125, -0.401);
  private readonly rpgHipRot = new THREE.Euler(0.08, 0.05, 0.02);
  private readonly rpgAdsPos = new THREE.Vector3(0.0, -0.095, -0.248);
  private readonly rpgAdsRot = new THREE.Euler(0.03, 0.0, 0.0);
  /** Pose-local muzzle / casing (hip vs ADS). Tuned in `?calib=mp5|ak47`. */
  private readonly mp5MuzzleHip = new THREE.Vector3(0.04, -0.02, -0.40);
  private readonly mp5MuzzleAds = new THREE.Vector3(0.00, -0.01, -0.36);
  private readonly mp5CasingHip = new THREE.Vector3(0.055, -0.02, -0.22);
  private readonly mp5CasingAds = new THREE.Vector3(0.04, -0.005, -0.20);
  private readonly rifleMuzzleHip = new THREE.Vector3(0.03, 0.00, -0.48);
  private readonly rifleMuzzleAds = new THREE.Vector3(0.00, 0.01, -0.44);
  private readonly rifleCasingHip = new THREE.Vector3(0.06, 0.00, -0.26);
  private readonly rifleCasingAds = new THREE.Vector3(0.045, 0.01, -0.24);
  private calibMuzzleMarker: THREE.Group | null = null;
  private calibCasingMarker: THREE.Mesh | null = null;
  private readonly baseFov = 54;
  private fovPunch = 0;

  private readonly onTuneShotgun: (e: Event) => void;

  private readonly impactRay = new THREE.Raycaster();
  private readonly shotOrigin = new THREE.Vector3();
  private readonly shotDir = new THREE.Vector3();
  private readonly shotRight = new THREE.Vector3();

  private onShot: ((e: ShotEvent) => void) | null = null;
  private onSmash: ((origin: THREE.Vector3) => void) | null = null;
  private onCrateBreak: ((crate: THREE.Object3D) => void) | null = null;
  private onLampBreak: ((lamp: THREE.Object3D) => void) | null = null;
  private onGrateBreak: ((obj: THREE.Object3D) => boolean) | null = null;
  private onAlarmBreak: ((obj: THREE.Object3D) => void) | null = null;
  private onThud: ((origin: THREE.Vector3) => void) | null = null;
  private toiletSpray: ToiletSpraySystem | null = null;
  /** Return true if the hit was consumed by an enemy (skip wall FX). */
  private onEnemyHit:
    | ((
        object: THREE.Object3D,
        damage: number,
        point: THREE.Vector3,
        dir: THREE.Vector3,
        weaponId: WeaponId,
        meleeStun?: number,
      ) => false | 'hit' | 'blocked')
    | null = null;

  constructor(private readonly audio: AudioManager) {
    this.camera = new THREE.PerspectiveCamera(this.baseFov, 1, 0.01, 10);
    this.camera.rotation.order = 'YXZ';
    this.impactRay.params.Line = { threshold: 0.02 };
    this.impactRay.params.Points = { threshold: 0.04 };

    this.scene.add(new THREE.AmbientLight(0xfff2e8, 1.35));
    const key = new THREE.DirectionalLight(0xffffff, 2.4);
    key.position.set(0.4, 1.2, 0.9);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffd0b0, 0.85);
    fill.position.set(-0.7, 0.4, 0.3);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffc090, 0.7);
    rim.position.set(0.1, 0.2, -1.2);
    this.scene.add(rim);
    const point = new THREE.PointLight(0xffe8d0, 1.6, 4);
    point.position.set(0.05, 0.15, 0.25);
    this.scene.add(point);

    this.scene.add(this.root);
    this.root.add(this.sway);
    this.sway.add(this.bob);
    this.bob.add(this.pose);
    this.pose.add(this.weaponSlot);
    this.reloadHandTarget.name = 'reload_hand';
    this.pose.add(this.reloadHandTarget);
    this.buildSprintArm();
    this.buildSlideLegs();
    this.bob.add(this.sprintArm);
    this.root.add(this.slideLegs);
    if (this.procMode) {
      this.procArms = new ProcArms();
      this.pose.add(this.procArms.root);
      this.sprintArm.visible = false;
    }

    // Temporary placeholder until weapons are built
    this.placeholder = new THREE.Group();
    const tip = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, 0.02, 0.08),
      new THREE.MeshStandardMaterial({ color: 0x444444, metalness: 0.8, roughness: 0.3 }),
    );
    tip.position.set(0.1, -0.1, -0.25);
    this.placeholder.add(tip);
    this.weaponSlot.add(this.placeholder);

    // Additive flash — depth-tested so it sits at the muzzle, not over the whole gun
    const flashMat = (color: number) =>
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: true,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
    this.muzzleFlashCore = new THREE.Mesh(new THREE.SphereGeometry(0.022, 10, 10), flashMat(0xfff0a0));
    this.muzzleFlashSpike = new THREE.Mesh(new THREE.ConeGeometry(0.028, 0.1, 8), flashMat(0xffcc66));
    this.muzzleFlashSpike.rotation.x = -Math.PI / 2;
    this.muzzleFlashSpike.position.z = -0.04;
    this.muzzleFlashSprite = new THREE.Mesh(
      new THREE.PlaneGeometry(0.055, 0.055),
      flashMat(0xffe080),
    );
    this.muzzleFlashGroup.add(this.muzzleFlashCore);
    this.muzzleFlashGroup.add(this.muzzleFlashSpike);
    this.muzzleFlashGroup.add(this.muzzleFlashSprite);
    this.muzzleFlashGroup.visible = false;
    this.pose.add(this.muzzleFlashGroup);

    this.muzzleLight = new THREE.PointLight(0xffb060, 0, 4.5, 1.6);
    this.pose.add(this.muzzleLight);

    // Lights the real world around the barrel (viewmodel light only hits the gun pass)
    this.worldMuzzleLight = new THREE.PointLight(0xffc070, 0, 12, 1.4);
    this.worldMuzzleLight.castShadow = false;

    this.casings = new THREE.Group();
    this.scene.add(this.casings);
    this.initImpactPools();

    this.pose.position.copy(this.hipPos);
    this.pose.rotation.copy(this.hipRot);

    // Live orientation tuner for calib screenshots
    this.onTuneShotgun = (e: Event) => {
      const d = (e as CustomEvent).detail as {
        rx: number;
        ry: number;
        rz: number;
        scale?: number;
        reportBBox?: boolean;
      };
      if (!this.shotgun) return;
      this.shotgun.oriented.rotation.order = 'YXZ';
      this.shotgun.oriented.rotation.set(d.rx, d.ry, d.rz);
      if (d.scale != null) this.shotgun.oriented.scale.setScalar(d.scale);
      this.shotgun.root.updateMatrixWorld(true);
      if (d.reportBBox) {
        const box = new THREE.Box3().setFromObject(this.shotgun.root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        window.dispatchEvent(
          new CustomEvent('vm-tune-shotgun-bbox', {
            detail: {
              size: size.toArray(),
              center: center.toArray(),
              min: box.min.toArray(),
              max: box.max.toArray(),
            },
          }),
        );
      }
    };
    window.addEventListener('vm-tune-shotgun', this.onTuneShotgun);
  }

  get isReady(): boolean {
    return this.ready;
  }

  get viewmodelKind(): 'PROC' | 'GLB' {
    return this.procMode ? 'PROC' : 'GLB';
  }

  async load(): Promise<void> {
    const texLoader = new THREE.TextureLoader();
    const [models, smokeMaps, hitMap] = await Promise.all([
      Promise.resolve(buildAllProcWeapons()),
      Promise.all(
        ['smoke_01.png', 'smoke_04.png', 'smoke_07.png'].map((name) =>
          texLoader.loadAsync(`/assets/textures/fx/smoke/${name}`),
        ),
      ),
      texLoader.loadAsync('/assets/textures/fx/explosions/T_vfx_hit122.jpg').catch(() => null),
    ]);
    this.pistol = models.pistol;
    this.shotgun = models.shotgun;
    this.sawedoff = models.sawedoff;
    this.mp5 = models.mp5;
    this.ak47 = models.ak47;
    this.punch = models.punch;
    this.gl = models.gl;
    this.rpg = models.rpg;
    this.smokeMaps = smokeMaps;
    for (const map of this.smokeMaps) {
      map.colorSpace = THREE.SRGBColorSpace;
    }
    if (hitMap) {
      hitMap.colorSpace = THREE.SRGBColorSpace;
      this.decalMat.map = hitMap;
      this.decalMat.needsUpdate = true;
    }
    this.smokeSpriteMat = new THREE.SpriteMaterial({
      map: this.smokeMaps[0] ?? null,
      color: 0x8a8478,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.weaponSlot.remove(this.placeholder);
    for (const id of WEAPON_ORDER) {
      const rig = this.rigFor(id);
      if (!rig) continue;
      this.weaponSlot.add(rig.root);
      rig.root.visible = id === this.weaponId;
    }
    this.syncArmStyle();
    this.syncPistolSilencer();

    this.ready = true;
  }

  /** Keep the world muzzle light in the graph so the first shot does not recompile every wall shader. */
  attachWorldFx(worldScene: THREE.Object3D): void {
    if (this.worldMuzzleLight.parent !== worldScene) {
      worldScene.add(this.worldMuzzleLight);
    }
    this.worldMuzzleLight.intensity = 0;
    this.worldMuzzleLight.visible = true;
  }

  /** Compile impact / flash / casing programs during load, not on the first trigger pull. */
  async warmGpu(
    renderer: THREE.WebGLRenderer,
    worldScene: THREE.Scene,
    camera: THREE.Camera,
  ): Promise<void> {
    this.attachWorldFx(worldScene);
    this.muzzleFlashGroup.visible = true;
    const core = this.muzzleFlashCore.material as THREE.MeshBasicMaterial;
    const spike = this.muzzleFlashSpike.material as THREE.MeshBasicMaterial;
    const sprite = this.muzzleFlashSprite.material as THREE.MeshBasicMaterial;
    core.opacity = 0.4;
    spike.opacity = 0.4;
    sprite.opacity = 0.4;

    const dummy = new THREE.Group();
    dummy.name = 'fxWarmup';
    dummy.position.set(0, -80, 0);
    dummy.add(new THREE.Mesh(this.decalGeo, this.decalMat));
    dummy.add(new THREE.Mesh(this.sparkGeo, this.sparkMat));
    dummy.add(new THREE.Mesh(this.debrisGeo, this.chipMat));
    dummy.add(new THREE.Mesh(this.casingGeo, this.casingMat));
    if (this.smokeSpriteMat) dummy.add(new THREE.Sprite(this.smokeSpriteMat));
    worldScene.add(dummy);

    try {
      // Sync compile — compileAsync hits program.isReady() on materials that
      // never got a currentProgram (mirrors, hidden avatar, unused FX).
      renderer.compile(worldScene, camera);
      renderer.compile(this.scene, this.camera);
    } catch (err) {
      console.warn('GPU warmup compile skipped', err);
    } finally {
      worldScene.remove(dummy);
      this.muzzleFlashGroup.visible = false;
      core.opacity = 0;
      spike.opacity = 0;
      sprite.opacity = 0;
      this.muzzleLight.intensity = 0;
      this.worldMuzzleLight.intensity = 0;
    }
  }

  setShotHandler(handler: (e: ShotEvent) => void): void {
    this.onShot = handler;
  }

  setSmashHandler(handler: (origin: THREE.Vector3) => void): void {
    this.onSmash = handler;
  }

  setCrateBreakHandler(handler: (crate: THREE.Object3D) => void): void {
    this.onCrateBreak = handler;
  }

  setLampBreakHandler(handler: (lamp: THREE.Object3D) => void): void {
    this.onLampBreak = handler;
  }

  setGrateBreakHandler(handler: (obj: THREE.Object3D) => boolean): void {
    this.onGrateBreak = handler;
  }

  setAlarmBreakHandler(handler: (obj: THREE.Object3D) => void): void {
    this.onAlarmBreak = handler;
  }

  setThudHandler(handler: (origin: THREE.Vector3) => void): void {
    this.onThud = handler;
  }

  setProjectileSystem(sys: ProjectileSystem): void {
    this.projectiles = sys;
  }

  setToiletSpray(sys: ToiletSpraySystem): void {
    this.toiletSpray = sys;
  }

  setShotLines(sys: ShotLineSystem): void {
    this.shotLines = sys;
  }

  private tryBreakCrate(obj: THREE.Object3D): boolean {
    const crate = findDestructibleCrate(obj);
    if (!crate || crate.userData.destroyed) return false;
    this.onCrateBreak?.(crate);
    return true;
  }

  private tryBreakGrate(obj: THREE.Object3D): boolean {
    if (!this.onGrateBreak) return false;
    let o: THREE.Object3D | null = obj;
    while (o) {
      if (o.userData?.destructible === 'grate' && !o.userData.destroyed) {
        return this.onGrateBreak(o);
      }
      o = o.parent;
    }
    return false;
  }

  private tryBreakAlarm(obj: THREE.Object3D): boolean {
    const panel = findDestructibleAlarm(obj);
    if (!panel || panel.userData.destroyed) return false;
    this.onAlarmBreak?.(panel);
    return true;
  }

  private tryBreakLamp(obj: THREE.Object3D): boolean {
    const lamp = findDestructibleLamp(obj);
    if (!lamp || lamp.userData.destroyed) return false;
    if (this.onLampBreak) {
      this.onLampBreak(lamp);
      return true;
    }
    destroyCeilingLamp(lamp);
    this.audio.play('lampBreak', { volume: 0.45, rate: 1.15 + Math.random() * 0.2 });
    return true;
  }

  private tryBurstToilet(obj: THREE.Object3D, smash = false): boolean {
    const toilet = findDestructibleToilet(obj);
    if (!toilet || toilet.userData.destroyed) return false;
    if (!burstToilet(toilet)) return false;
    const origin = new THREE.Vector3();
    const local = toilet.userData.sprayOrigin as THREE.Vector3 | undefined;
    if (local) {
      origin.copy(local);
      toilet.localToWorld(origin);
    } else {
      toilet.getWorldPosition(origin);
      origin.y += 0.4;
    }
    const stopLoop = this.audio.playLoopAt('waterLeak', origin, {
      volume: 0.2,
      rate: 1.12,
      range: 16,
      refDistance: 2.4,
    });
    this.toiletSpray?.start(origin, { duration: 5.2, stopLoop });
    this.audio.play('destroyToilet', { volume: 0.85, rate: 0.96 + Math.random() * 0.08 });
    if (smash) this.onSmash?.(origin.clone());
    return true;
  }

  /** Shatter every intact glass pane whose center is inside the blast radius. */
  shatterInRadius(origin: THREE.Vector3, radius: number, worldScene: THREE.Object3D): void {
    const panes: THREE.Object3D[] = [];
    worldScene.traverse((o) => {
      if (o.userData?.destructible !== 'glass' || o.userData.destroyed) return;
      const c = new THREE.Vector3();
      o.getWorldPosition(c);
      if (c.distanceTo(origin) <= radius + 0.25) panes.push(o);
    });
    for (const pane of panes) {
      const box = new THREE.Box3().setFromObject(pane);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const n = new THREE.Vector3();
      if (size.x <= size.y && size.x <= size.z) n.set(1, 0, 0);
      else if (size.z <= size.x && size.z <= size.y) n.set(0, 0, 1);
      else n.set(0, 1, 0);
      pane.updateWorldMatrix(true, false);
      n.transformDirection(pane.matrixWorld).normalize();
      const inbound = center.clone().sub(origin);
      if (inbound.lengthSq() < 1e-6) inbound.copy(n);
      else inbound.normalize();
      this.shatterGlass(pane, origin.clone().lerp(center, 0.65), n, inbound, worldScene);
    }
  }

  setEnemyHitHandler(
    handler: (
      object: THREE.Object3D,
      damage: number,
      point: THREE.Vector3,
      dir: THREE.Vector3,
      weaponId: WeaponId,
      meleeStun?: number,
    ) => false | 'hit' | 'blocked',
  ): void {
    this.onEnemyHit = handler;
  }

  get currentWeapon(): WeaponDef {
    return WEAPONS[this.weaponId];
  }

  get adsBlend(): number {
    return this.ads;
  }

  /** Guard up, or fists still raised after a recent punch. */
  get fistsUp(): boolean {
    return (
      this.weaponId === 'punch' &&
      this.punchTiredT <= 0 &&
      (this.punchHotT > 0 || this.punchStrike != null || this.ads > 0.2)
    );
  }

  get holdSprintBlend(): number {
    return Math.max(this.sprintBlend, this.sprintArmBlend);
  }

  get punchStrikeId(): PunchClipId | null {
    return this.punchStrike;
  }

  get punchPhase(): number {
    if (!this.punchStrike) return 0;
    const clip = this.punchClips[this.punchStrike];
    const dur = this.punchFrozenKey ? Math.max(0.05, clip.duration) : punchPlayDuration(clip);
    return Math.min(1, this.punchT / Math.max(1e-4, dur));
  }

  /** Weapon root in viewmodel-camera space (same hold you see in first person). */
  readGunInCamera(pos: THREE.Vector3, quat: THREE.Quaternion): boolean {
    const gun = this.activeRig()?.root ?? this.weaponSlot;
    this.camera.updateMatrixWorld(true);
    gun.updateWorldMatrix(true, true);
    _gunCamM.copy(this.camera.matrixWorld).invert().multiply(gun.matrixWorld);
    _gunCamM.decompose(pos, quat, _gunCamS);
    return true;
  }

  /** `bob` in viewmodel-camera space (sway included). */
  readHoldInCamera(pos: THREE.Vector3, quat: THREE.Quaternion): void {
    this.camera.updateMatrixWorld(true);
    this.bob.updateWorldMatrix(true, true);
    _gunCamM.copy(this.camera.matrixWorld).invert().multiply(this.bob.matrixWorld);
    _gunCamM.decompose(pos, quat, _gunCamS);
  }

  /** Deep clone of the live FPS hold (arms + gun). Materials are copied so the FP pass is untouched. */
  cloneHoldVisual(): THREE.Group {
    const clone = this.bob.clone(true);
    clone.name = 'avatarFpsHold';
    clone.traverse((o) => {
      o.userData.ignoreHit = true;
      o.userData.noHit = true;
      if ((o as THREE.Light).isLight) {
        o.visible = false;
        return;
      }
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = false;
      mesh.frustumCulled = false;
      const mat = mesh.material;
      if (mat instanceof THREE.MeshStandardMaterial) {
        mesh.material = new THREE.MeshBasicMaterial({
          color: mat.color.clone(),
          map: mat.map,
          fog: true,
          transparent: mat.transparent,
          opacity: mat.opacity,
        });
      }
    });
    return clone;
  }

  holdTreeMatches(dst: THREE.Object3D): boolean {
    return holdTreesMatch(this.bob, dst);
  }

  /** Copy the live FPS pose/animation onto a clone from `cloneHoldVisual`. */
  syncHoldVisual(dst: THREE.Group): void {
    copyHoldTree(this.bob, dst);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  update(player: PlayerController, input: Input, dt: number, worldScene: THREE.Object3D): void {
    if (!this.ready) return;

    this.worldScene = worldScene;
    this.playerCamera = player.camera;
    this.groundY = player.body.position.y;
    const def = this.currentWeapon;

    if (this.calibFrozen) {
      this.updateCalibFrame(player, def, input, dt, worldScene);
      return;
    }
    this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    this.punchComboWindow = Math.max(0, this.punchComboWindow - dt);
    this.punchTiredT = Math.max(0, this.punchTiredT - dt);
    this.breathPhase += dt * 1.4;
    this.sprintFireRaiseT = Math.max(0, this.sprintFireRaiseT - dt);

    for (const { slot, ids } of WEAPON_SLOTS) {
      if (!input.wasPressed(`Digit${slot}`)) continue;
      const id = pickSlotWeapon(ids, (w) => this.owned.has(w));
      if (!id) continue;
      if (id === 'pistol' && this.weaponId === 'pistol') {
        this.togglePistolSilencer();
        continue;
      }
      this.switchWeapon(id);
    }
    const wheel = input.consumeWheel();
    if (wheel !== 0) {
      const ownedOrder = WEAPON_ORDER.filter((id) => this.owned.has(id));
      if (ownedOrder.length > 1) {
        const idx = Math.max(0, ownedOrder.indexOf(this.weaponId));
        const next = ownedOrder[(idx + (wheel > 0 ? 1 : -1) + ownedOrder.length) % ownedOrder.length]!;
        this.switchWeapon(next);
      }
    }

    const tired = this.punchTiredT > 0;
    if (this.weaponId === 'punch') {
      if (!tired && (this.punchStrike || input.adsHeld)) this.punchHotT = 2.8;
      else this.punchHotT = Math.max(0, this.punchHotT - dt);
    } else {
      this.punchHotT = 0;
    }
    const moving = Math.hypot(player.body.velocity.x, player.body.velocity.z) > 0.5;
    const wantSprint =
      this.weaponId !== 'punch' &&
      (tired ||
        (input.sprinting &&
          moving &&
          player.body.grounded &&
          !input.adsHeld &&
          !player.isSliding &&
          player.sprintSuppressT <= 0 &&
          this.sprintFireRaiseT <= 0 &&
          this.animState !== 'reloading'));
    // Snap out of sprint pose quickly when canceling to hip-fire
    const sprintDamp = this.sprintFireRaiseT > 0 ? 22 : tired ? 10 : 8;
    this.sprintBlend = damp(this.sprintBlend, wantSprint ? 1 : 0, sprintDamp, dt);
    this.sprintArmBlend = damp(this.sprintArmBlend, wantSprint ? 1 : 0, sprintDamp, dt);
    this.slideLegBlend = damp(
      this.slideLegBlend,
      player.isSliding || player.slideBlend > 0.08 ? 1 : 0,
      14,
      dt,
    );
    if (wantSprint) {
      const run = tired ? 5.5 : 10 + Math.hypot(player.body.velocity.x, player.body.velocity.z) * 0.9;
      this.sprintRunPhase += dt * run;
    }

    const canAds =
      !tired &&
      (this.weaponId === 'punch' || this.sprintBlend < 0.4) &&
      this.animState !== 'reloading' &&
      this.animState !== 'switching' &&
      !player.isSliding;
    this.ads =
      this.debugAdsLock != null
        ? damp(this.ads, this.debugAdsLock, 12, dt)
        : damp(this.ads, canAds && input.adsHeld ? 1 : 0, 12, dt);

    player.lookSensitivityScale = lerp(1, def.adsSensScale, this.ads);
    player.setFovBlend(this.ads, def.adsFov);
    this.fovPunch = damp(this.fovPunch, 0, 14, dt);
    this.camera.fov = lerp(this.baseFov, def.adsFov - 12, this.ads) + this.fovPunch;
    this.camera.updateProjectionMatrix();

    if (this.animState === 'idle' || this.animState === 'firing') {
      if (input.reloadPressed && def.requiresReload) this.beginReload();
      const wantsFire = def.automatic ? input.fireHeld : input.firePressed;
      if (wantsFire && this.fireCooldown <= 0 && !tired) {
        // Sprint + fire: cancel run briefly, snap gun to hip, shoot
        if (this.weaponId !== 'punch' && !player.isSliding && this.sprintBlend >= 0.4) {
          const suppress = isShotgunLike(this.weaponId) ? 0.45 : 0.32;
          player.sprintSuppressT = Math.max(player.sprintSuppressT, suppress);
          this.sprintFireRaiseT = isShotgunLike(this.weaponId) ? 0.4 : 0.28;
          this.sprintBlend = 0;
          this.sprintArmBlend = 0;
        }
        const canShoot =
          this.weaponId === 'punch' ||
          player.isSliding ||
          this.sprintBlend < 0.55 ||
          this.sprintFireRaiseT > 0;
        if (canShoot) this.tryFire(player, worldScene);
      }
    }

    this.updateAnim(dt);
    this.syncRpgRocket();
    if (this.weaponId === 'punch') this.applyPunchFists();
    this.updatePose(player, dt);
    this.updateProcMotion(dt);
    if (this.procMode && this.procArms) {
      this.pose.updateWorldMatrix(true, true);
      const rig = this.activeRig();
      this.procArms.update(
        rig?.gripR ?? null,
        this.sprintArmBlend > 0.28 ? null : (this.reloadLeftGrip() ?? rig?.gripL ?? null),
        this.sprintArmBlend,
        this.sprintRunPhase,
        this.punchAds(),
        this.reloadLeftPose(),
      );
    } else {
      this.updateSprintArm(dt);
    }
    this.updateSlideLegs(dt);
    this.updateFlash(dt);
    this.updateSmoke(dt);
    this.updateImpactFX(dt);
    this.updateCasings(dt);
    this.recoil = damp(this.recoil, 0, def.recoilRecovery, dt);
    this.kickPitch = damp(this.kickPitch, 0, def.recoilRecovery * 1.2, dt);
    this.kickYaw = damp(this.kickYaw, 0, def.recoilRecovery, dt);
  }

  private updateProcMotion(dt: number): void {
    this.applyShotgunPump();
    if (!this.procMode) return;
    const rig = this.activeRig();
    if (rig?.slide) {
      rig.slide.position.z = this.recoil * 0.028;
    }
    this.updateDoubleBarrelReload(dt);
    this.updateMagReload(dt);
  }

  private applyShotgunPump(): void {
    if (!this.shotgun) return;
    if (this.weaponId === 'shotgun' && this.shotgun.pump) {
      this.shotgun.pump.position.z = this.pumpAmount * 0.17;
      this.shotgun.root.position.z = this.pumpAmount * 0.032;
    } else {
      this.shotgun.root.position.z = 0;
      if (this.shotgun.pump) this.shotgun.pump.position.z = 0;
    }
  }

  private sampleReload(t: number): DoubleReloadBeat {
    if (this.reloadCalibHold && this.reloadCalibBeat) {
      const beat = this.reloadClip.find((b) => b.id === this.reloadCalibBeat);
      if (beat) {
        copyReloadBeat(beat, this.reloadSample);
        return this.reloadSample;
      }
    }
    return sampleReloadClip(this.reloadClip, t, this.reloadSample);
  }

  private doubleReloadLeftGrip(): THREE.Object3D | null {
    if (this.weaponId !== 'sawedoff' || this.animState !== 'reloading') return null;
    const t = this.animT / WEAPONS.sawedoff.reloadTime;
    if (this.sampleReload(t).dummyBlend > 0.12) return this.reloadHandTarget;
    return null;
  }

  private magReloadId(): MagReloadWeaponId | null {
    return isMagReloadWeapon(this.weaponId) ? this.weaponId : null;
  }

  private magReloadLeftGrip(): THREE.Object3D | null {
    const id = this.magReloadId();
    if (!id || this.animState !== 'reloading') return null;
    const t = this.animT / WEAPONS[id].reloadTime;
    if (this.sampleMagReload(t).dummyBlend > 0.12) return this.reloadHandTarget;
    return null;
  }

  private reloadLeftGrip(): THREE.Object3D | null {
    return this.doubleReloadLeftGrip() ?? this.magReloadLeftGrip();
  }

  private reloadLeftPose() {
    if (this.magReloadLeftGrip()) return this.magSample.hand;
    if (this.doubleReloadLeftGrip()) return this.reloadSample.hand;
    return null;
  }

  private sampleMagReload(t: number): PistolReloadBeat {
    const id = this.magReloadId() ?? 'pistol';
    if (this.reloadCalibHold && this.magCalibBeat) {
      const beat = this.magClips[id].find((b) => b.id === this.magCalibBeat);
      if (beat) {
        copyPistolReloadBeat(beat, this.magSample);
        return this.magSample;
      }
    }
    return samplePistolReloadClip(this.magClips[id], t, this.magSample);
  }

  private applyMagReloadGunSample(pos: THREE.Vector3, rot: THREE.Euler, t: number): void {
    const g = this.sampleMagReload(t).gun;
    pos.x += g.x;
    pos.y += g.y;
    pos.z += g.z;
    rot.x += g.pitch;
    rot.y += g.yaw;
    rot.z += g.roll;
  }

  private applyReloadGunSample(pos: THREE.Vector3, rot: THREE.Euler, t: number): void {
    const g = this.sampleReload(t).gun;
    pos.x += g.x;
    pos.y += g.y;
    pos.z += g.z;
    rot.x += g.pitch;
    rot.y += g.yaw;
    rot.z += g.roll;
  }

  private resetDoubleBarrelIdle(): void {
    const br = this.sawedoff?.barrelBreak;
    if (br) br.rotation.x = 0;
    if (this.sawedoff?.shellL) this.sawedoff.shellL.visible = true;
    if (this.sawedoff?.shellR) this.sawedoff.shellR.visible = true;
    if (this.reloadPair) this.reloadPair.visible = false;
    this.doubleReloadEjected = false;
    this.doubleReloadInserted = false;
    this.doubleReloadCloseSfx = false;
    this.reloadCalibHold = false;
    this.reloadCalibBeat = null;
  }

  private resetMagReloadIdle(): void {
    for (const id of ['pistol', 'mp5', 'ak47'] as const) {
      const mag = this.rigFor(id)?.mag;
      if (mag) mag.visible = true;
    }
    if (this.magHeld) this.magHeld.visible = false;
    this.magDropped = false;
    this.magInserted = false;
    this.magSeatSfx = false;
    this.magCalibBeat = null;
  }

  private ensureReloadPair(): THREE.Group {
    if (!this.reloadPair) {
      this.reloadPair = makeDoubleReloadPair();
    }
    if (this.reloadPair.parent !== this.reloadHandTarget) {
      this.reloadHandTarget.add(this.reloadPair);
    }
    return this.reloadPair;
  }

  private clearReloadFlyers(): void {
    for (const flyer of this.reloadFlyers) {
      flyer.parent?.remove(flyer);
    }
    this.reloadFlyers.length = 0;
  }

  private spawnBreakEject(): void {
    const shellL = this.sawedoff?.shellL;
    const shellR = this.sawedoff?.shellR;
    if (!shellL || !shellR) return;
    this.sawedoff!.root.updateMatrixWorld(true);
    const sides: Array<[THREE.Object3D, number]> = [
      [shellL, -1],
      [shellR, 1],
    ];
    for (const [src, side] of sides) {
      const flyer = src.clone(true);
      flyer.visible = true;
      src.getWorldPosition(this.reloadTmpV);
      src.getWorldQuaternion(this.reloadTmpQ);
      flyer.position.copy(this.reloadTmpV);
      flyer.quaternion.copy(this.reloadTmpQ);
      this.reloadTmpV2.set(0, 0, 1).applyQuaternion(this.reloadTmpQ);
      const vel = this.reloadTmpV2.clone().multiplyScalar(1.9);
      vel.y += 0.7;
      vel.x += side * 0.22;
      flyer.userData.vel = vel;
      flyer.userData.spin = new THREE.Vector3(
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 9,
        side * 7,
      );
      flyer.userData.life = 0.95;
      this.scene.add(flyer);
      this.reloadFlyers.push(flyer);
      src.visible = false;
    }
    this.audio.play('casing', { volume: 0.28, rate: 0.85 });
    this.audio.play('casing', { volume: 0.24, rate: 1.05 });
  }

  private updateReloadFlyers(dt: number): void {
    for (let i = this.reloadFlyers.length - 1; i >= 0; i--) {
      const flyer = this.reloadFlyers[i]!;
      const vel = flyer.userData.vel as THREE.Vector3;
      vel.y -= 8.5 * dt;
      flyer.position.addScaledVector(vel, dt);
      const spin = flyer.userData.spin as THREE.Vector3 | undefined;
      if (spin) {
        flyer.rotation.x += spin.x * dt;
        flyer.rotation.y += spin.y * dt;
        flyer.rotation.z += spin.z * dt;
      }
      flyer.userData.life -= dt;
      if (flyer.userData.life <= 0) {
        flyer.parent?.remove(flyer);
        this.reloadFlyers.splice(i, 1);
      }
    }
  }

  private applyReloadDummy(t: number): void {
    const s = this.sampleReload(t);
    const dummy = this.reloadHandTarget;
    dummy.rotation.order = 'YXZ';
    dummy.rotation.set(s.dummy.pitch, s.dummy.yaw, s.dummy.roll);
    dummy.position.set(s.dummy.x, s.dummy.y, s.dummy.z);

    const follow = s.followGrip;
    const grip = this.sawedoff?.gripL;
    const parent = dummy.parent;
    if (follow > 0.001 && grip && parent) {
      parent.updateMatrixWorld(true);
      grip.updateMatrixWorld(true);
      grip.getWorldPosition(this.reloadTmpV);
      parent.worldToLocal(this.reloadTmpV);
      dummy.position.x += this.reloadTmpV.x * follow;
      dummy.position.y += this.reloadTmpV.y * follow;
      dummy.position.z += this.reloadTmpV.z * follow;
      parent.getWorldQuaternion(this.reloadTmpQ).invert();
      grip.getWorldQuaternion(this.reloadTmpQ2);
      this.reloadTmpQ.multiply(this.reloadTmpQ2);
      dummy.quaternion.slerp(this.reloadTmpQ, follow);
    }

    const pair = this.ensureReloadPair();
    pair.position.set(s.pair.x, s.pair.y, s.pair.z);
    pair.rotation.order = 'YXZ';
    pair.rotation.set(s.pair.rx, s.pair.ry, s.pair.rz);
    pair.visible = s.pairVis > 0.45 && !this.doubleReloadInserted;
  }

  private updateDoubleBarrelReload(dt: number): void {
    this.updateReloadFlyers(dt);
    const br = this.sawedoff?.barrelBreak;
    if (!br) return;
    if (this.weaponId !== 'sawedoff' || this.animState !== 'reloading') {
      if (this.weaponId !== 'sawedoff' || this.animState !== 'switching') {
        br.rotation.x = 0;
        if (this.sawedoff?.shellL) this.sawedoff.shellL.visible = true;
        if (this.sawedoff?.shellR) this.sawedoff.shellR.visible = true;
      }
      if (this.reloadPair) this.reloadPair.visible = false;
      return;
    }

    const t = clamp(this.animT / WEAPONS.sawedoff.reloadTime, 0, 1);
    const s = this.sampleReload(t);
    br.rotation.x = s.breakX;

    if (s.chamberVis < 0.5 && !this.doubleReloadEjected) {
      this.doubleReloadEjected = true;
      if (this.reloadCalibHold) {
        if (this.sawedoff?.shellL) this.sawedoff.shellL.visible = false;
        if (this.sawedoff?.shellR) this.sawedoff.shellR.visible = false;
      } else {
        this.spawnBreakEject();
      }
    }

    const shutT = this.reloadClip.find((b) => b.id === 'shut')?.t ?? 0.64;
    if (t >= shutT && !this.doubleReloadCloseSfx) {
      this.doubleReloadCloseSfx = true;
      if (!this.reloadCalibHold) this.audio.play('shotgunPump', { volume: 0.78, rate: 1.05 });
    }

    this.applyReloadDummy(t);

    if (s.chamberVis >= 0.5 && this.doubleReloadEjected && !this.doubleReloadInserted) {
      this.doubleReloadInserted = true;
      if (this.reloadPair) this.reloadPair.visible = false;
      if (this.sawedoff?.shellL) this.sawedoff.shellL.visible = true;
      if (this.sawedoff?.shellR) this.sawedoff.shellR.visible = true;
    } else if (!this.doubleReloadInserted && this.doubleReloadEjected) {
      if (this.sawedoff?.shellL) this.sawedoff.shellL.visible = false;
      if (this.sawedoff?.shellR) this.sawedoff.shellR.visible = false;
    }
  }

  private ensureMagHeld(): THREE.Group {
    const id = this.magReloadId() ?? 'pistol';
    if (this.magHeld && this.magHeldKind !== id) {
      this.magHeld.parent?.remove(this.magHeld);
      this.magHeld = null;
      this.magHeldKind = null;
    }
    if (!this.magHeld) {
      this.magHeld =
        id === 'mp5'
          ? makeMp5Mag()
          : id === 'ak47'
            ? makeAk47Mag()
            : makePistolMag(this.pistol ? readM9GripShape(this.pistol) : undefined);
      this.magHeldKind = id;
    }
    if (this.magHeld.parent !== this.reloadHandTarget) {
      this.reloadHandTarget.add(this.magHeld);
    }
    return this.magHeld;
  }

  private spawnMagDrop(): void {
    const mag = this.activeRig()?.mag;
    if (!mag) return;
    mag.updateMatrixWorld(true);
    const flyer = mag.clone(true);
    flyer.visible = true;
    mag.getWorldPosition(this.reloadTmpV);
    mag.getWorldQuaternion(this.reloadTmpQ);
    flyer.position.copy(this.reloadTmpV);
    flyer.quaternion.copy(this.reloadTmpQ);
    this.reloadTmpV2.set(0, -1, 0.15).applyQuaternion(this.reloadTmpQ);
    const vel = this.reloadTmpV2.clone().multiplyScalar(1.35);
    vel.y -= 0.55;
    flyer.userData.vel = vel;
    flyer.userData.spin = new THREE.Vector3(
      (Math.random() - 0.5) * 8,
      (Math.random() - 0.5) * 6,
      (Math.random() - 0.5) * 10,
    );
    flyer.userData.life = 0.9;
    this.scene.add(flyer);
    this.reloadFlyers.push(flyer);
    mag.visible = false;
    this.audio.play('casing', { volume: 0.22, rate: 1.15 });
  }

  private applyMagDummy(t: number): void {
    const s = this.sampleMagReload(t);
    const dummy = this.reloadHandTarget;
    dummy.rotation.order = 'YXZ';
    dummy.rotation.set(s.dummy.pitch, s.dummy.yaw, s.dummy.roll);
    dummy.position.set(s.dummy.x, s.dummy.y, s.dummy.z);

    const follow = s.followGrip;
    const grip = this.activeRig()?.gripL;
    const parent = dummy.parent;
    if (follow > 0.001 && grip && parent) {
      parent.updateMatrixWorld(true);
      grip.updateMatrixWorld(true);
      grip.getWorldPosition(this.reloadTmpV);
      parent.worldToLocal(this.reloadTmpV);
      dummy.position.x += this.reloadTmpV.x * follow;
      dummy.position.y += this.reloadTmpV.y * follow;
      dummy.position.z += this.reloadTmpV.z * follow;
      parent.getWorldQuaternion(this.reloadTmpQ).invert();
      grip.getWorldQuaternion(this.reloadTmpQ2);
      this.reloadTmpQ.multiply(this.reloadTmpQ2);
      dummy.quaternion.slerp(this.reloadTmpQ, follow);
    }

    const held = this.ensureMagHeld();
    held.position.set(s.held.x, s.held.y, s.held.z);
    held.rotation.order = 'YXZ';
    held.rotation.set(s.held.pitch, s.held.yaw, s.held.roll);
    held.visible = s.magHold > 0.45 && s.magSeated < 0.5;
  }

  private updateMagReload(_dt: number): void {
    const id = this.magReloadId();
    const mag = this.activeRig()?.mag;
    if (!id || this.animState !== 'reloading') {
      if (!id || this.animState !== 'switching') {
        if (mag) mag.visible = true;
      }
      if (this.magHeld) this.magHeld.visible = false;
      return;
    }

    const t = clamp(this.animT / WEAPONS[id].reloadTime, 0, 1);
    const s = this.sampleMagReload(t);
    const seated = s.magSeated >= 0.5;

    if (!seated && !this.magDropped) {
      this.magDropped = true;
      if (id === 'pistol' && !this.reloadCalibHold) {
        this.spawnMagDrop();
      } else {
        if (mag) mag.visible = false;
        if (id !== 'pistol' && !this.reloadCalibHold) {
          this.audio.play('casing', { volume: 0.2, rate: 1.05 });
        }
      }
    }

    this.applyMagDummy(t);

    if (seated) {
      if (mag) mag.visible = true;
      if (this.magDropped && !this.magInserted) {
        this.magInserted = true;
        if (!this.magSeatSfx && !this.reloadCalibHold) {
          this.magSeatSfx = true;
          this.audio.play('casing', { volume: 0.28, rate: 1.45 });
        }
      }
    } else if (this.magDropped && mag) {
      mag.visible = false;
    }
  }

  private rigFor(id: WeaponId): LoadedViewWeapon | null {
    switch (id) {
      case 'pistol':
        return this.pistol;
      case 'shotgun':
        return this.shotgun;
      case 'sawedoff':
        return this.sawedoff;
      case 'mp5':
        return this.mp5;
      case 'ak47':
        return this.ak47;
      case 'punch':
        return this.punch;
      case 'gl':
        return this.gl;
      case 'rpg':
        return this.rpg;
    }
  }

  private switchWeapon(id: WeaponId): void {
    if (!this.ready) return;
    if (!this.owned.has(id)) return;
    if (!this.rigFor(id)) return;

    // Mid-switch: retarget while holstering, otherwise queue after draw
    if (this.animState === 'switching') {
      const goingTo = this.switchTarget ?? this.weaponId;
      if (id === goingTo) {
        this.pendingWeapon = null;
        return;
      }
      if (!this.switchSwapped) this.switchTarget = id;
      else this.pendingWeapon = id;
      return;
    }

    if (id === this.weaponId) return;

    this.pumpAmount = 0;
    this.pendingSawedReload = false;
    this.pendingRpgReload = false;
    this.rpgLoadClick = false;
    this.pendingWeapon = null;
    this.switchTarget = id;
    this.switchSwapped = false;
    this.animState = 'switching';
    this.animT = 0;
    this.resetDoubleBarrelIdle();
    this.resetMagReloadIdle();
    this.punchComboStep = 0;
    this.punchComboWindow = 0;
  }

  get hudWeaponName(): string {
    if (this.weaponId !== 'pistol') return this.currentWeapon.name;
    return this.pistolSilenced ? 'Pistol with silencer' : 'Pistol without silencer';
  }

  consumeToast(): string | null {
    const t = this.pendingToast;
    this.pendingToast = null;
    return t;
  }

  private togglePistolSilencer(): void {
    if (this.animState === 'switching') return;
    this.pistolSilenced = !this.pistolSilenced;
    this.syncPistolSilencer();
    this.pendingToast = this.pistolSilenced ? 'Pistol with silencer' : 'Pistol without silencer';
    this.audio.play('switchClick', { volume: 0.48, rate: this.pistolSilenced ? 0.92 : 1.08 });
  }

  private syncPistolSilencer(): void {
    const s = this.pistol?.silencer;
    if (s) s.visible = this.pistolSilenced;
  }

  private syncRpgRocket(): void {
    const rocket = this.rpg?.rocket;
    if (!rocket) return;
    const a = this.ammo.rpg;
    if (this.animState === 'reloading' && this.weaponId === 'rpg') {
      rocket.visible = this.animT / WEAPONS.rpg.reloadTime >= 0.38 && a.reserve > 0;
      return;
    }
    rocket.visible = a.mag > 0;
  }

  private applyWeaponVisibility(): void {
    for (const id of WEAPON_ORDER) {
      const rig = this.rigFor(id);
      if (rig) rig.root.visible = this.weaponId === id;
    }
    if (this.weaponId === 'punch') {
      this.sprintBlend = 0;
      this.sprintArmBlend = 0;
    }
    this.syncArmStyle();
  }

  private syncArmStyle(): void {
    if (!this.procArms) return;
    this.procArms.useWeapon(this.weaponId);
  }

  private beginReload(): void {
    const def = this.currentWeapon;
    if (!def.requiresReload) return;
    if (def.id === 'sawedoff') {
      this.startSawedReload(false);
      return;
    }
    if (isMagReloadWeapon(def.id)) {
      this.startMagReload(false);
      return;
    }
    const a = this.ammo[def.id];
    if (a.mag >= def.magSize || a.reserve <= 0) return;
    this.animState = 'reloading';
    this.animT = 0;
    this.reloadPreview = false;
    if (def.id === 'rpg') {
      this.rpgLoadClick = false;
      this.audio.play('switchClick', { volume: 0.42, rate: 1.15 });
    }
  }

  /** Same break-reload clip as R, but ignores mag/reserve so it can loop. */
  playDoubleReloadPreview(): void {
    if (this.weaponId !== 'sawedoff') return;
    this.reloadCalibHold = false;
    this.reloadCalibBeat = null;
    this.startSawedReload(true);
  }

  /** Rack + side eject, no shot. Studio / HUD preview. */
  playPumpPreview(): void {
    if (this.weaponId !== 'shotgun' || !this.ready || !this.shotgun?.pump) return;
    this.animState = 'pumping';
    this.animT = 0;
    this.pumpAmount = 0;
  }

  playReloadPreview(): void {
    if (this.weaponId === 'sawedoff') this.playDoubleReloadPreview();
    else if (this.weaponId === 'shotgun') this.playPumpPreview();
    else if (isMagReloadWeapon(this.weaponId)) this.playMagReloadPreview();
  }

  playMagReloadPreview(): void {
    if (!isMagReloadWeapon(this.weaponId)) return;
    this.reloadCalibHold = false;
    this.magCalibBeat = null;
    this.startMagReload(true);
  }

  /** @deprecated use playMagReloadPreview */
  playPistolReloadPreview(): void {
    this.playMagReloadPreview();
  }

  freezeReloadBeat(id: DoubleReloadBeatId): void {
    if (this.weaponId !== 'sawedoff') return;
    const beat = this.reloadClip.find((b) => b.id === id);
    if (!beat) return;
    this.startSawedReload(true, true);
    this.reloadCalibHold = true;
    this.reloadCalibBeat = id;
    this.animT = WEAPONS.sawedoff.reloadTime * beat.t;
  }

  freezePistolReloadBeat(id: PistolReloadBeatId): void {
    const wid = this.magReloadId();
    if (!wid) return;
    const beat = this.magClips[wid].find((b) => b.id === id);
    if (!beat) return;
    this.startMagReload(true, true);
    this.reloadCalibHold = true;
    this.magCalibBeat = id;
    this.animT = WEAPONS[wid].reloadTime * beat.t;
  }

  /** Back to hip: left hand on the forend / grip, barrels shut, mag seated. */
  clearReloadFreeze(): void {
    this.animState = 'idle';
    this.animT = 0;
    this.reloadPreview = false;
    this.resetDoubleBarrelIdle();
    this.resetMagReloadIdle();
    this.clearReloadFlyers();
  }

  /** @deprecated use freezeReloadBeat('hold') */
  freezeDoubleReloadHold(): void {
    this.freezeReloadBeat('hold');
  }

  get reloadHoldFrozen(): boolean {
    return this.reloadCalibHold;
  }

  get reloadFrozenBeat(): DoubleReloadBeatId | null {
    return this.reloadCalibBeat;
  }

  get pistolReloadFrozenBeat(): PistolReloadBeatId | null {
    return this.magCalibBeat;
  }

  reloadBeat(id: DoubleReloadBeatId): DoubleReloadBeat | null {
    return this.reloadClip.find((b) => b.id === id) ?? null;
  }

  pistolReloadBeat(id: PistolReloadBeatId): PistolReloadBeat | null {
    const wid = this.magReloadId();
    if (!wid) return null;
    return this.magClips[wid].find((b) => b.id === id) ?? null;
  }

  get reloadBeats(): DoubleReloadBeat[] {
    return this.reloadClip;
  }

  get pistolReloadBeats(): PistolReloadBeat[] {
    const wid = this.magReloadId() ?? 'pistol';
    return this.magClips[wid];
  }

  resetReloadBeat(id: DoubleReloadBeatId): void {
    const baked = bakedReloadClip().find((b) => b.id === id);
    const live = this.reloadBeat(id);
    if (baked && live) copyReloadBeat(baked, live);
  }

  resetPistolReloadBeat(id: PistolReloadBeatId): void {
    const wid = this.magReloadId();
    if (!wid) return;
    const baked = bakedMagReloadClip(wid).find((b) => b.id === id);
    const live = this.pistolReloadBeat(id);
    if (baked && live) copyPistolReloadBeat(baked, live);
  }

  resetReloadClip(): void {
    const fresh = bakedReloadClip();
    for (const b of fresh) {
      const live = this.reloadBeat(b.id);
      if (live) copyReloadBeat(b, live);
    }
  }

  resetPistolReloadClip(): void {
    const wid = this.magReloadId();
    if (!wid) return;
    const fresh = bakedMagReloadClip(wid);
    for (const b of fresh) {
      const live = this.pistolReloadBeat(b.id);
      if (live) copyPistolReloadBeat(b, live);
    }
  }

  resetReloadHoldTune(): void {
    this.resetReloadClip();
    for (const id of ['pistol', 'mp5', 'ak47'] as const) {
      const fresh = bakedMagReloadClip(id);
      for (const b of fresh) {
        const live = this.magClips[id].find((x) => x.id === b.id);
        if (live) copyPistolReloadBeat(b, live);
      }
    }
  }

  private startSawedReload(preview: boolean, quiet = false): void {
    if (!this.ready || !this.sawedoff?.barrelBreak) return;
    if (!preview) {
      if (this.animState === 'reloading') return;
      const a = this.ammo.sawedoff;
      const def = WEAPONS.sawedoff;
      if (a.mag >= def.magSize || a.reserve <= 0) return;
    }
    this.reloadPreview = preview;
    this.reloadCalibHold = false;
    this.reloadCalibBeat = null;
    this.magCalibBeat = null;
    this.animState = 'reloading';
    this.animT = 0;
    this.doubleReloadEjected = false;
    this.doubleReloadInserted = false;
    this.doubleReloadCloseSfx = false;
    this.clearReloadFlyers();
    if (this.reloadPair) this.reloadPair.visible = false;
    if (!quiet) this.audio.play('shotgunPump', { volume: 0.7, rate: 0.72 });
  }

  private startMagReload(preview: boolean, quiet = false): void {
    const id = this.magReloadId();
    if (!id || !this.ready || !this.rigFor(id)?.mag) return;
    if (!preview) {
      if (this.animState === 'reloading') return;
      const a = this.ammo[id];
      const def = WEAPONS[id];
      if (a.mag >= def.magSize || a.reserve <= 0) return;
    }
    this.reloadPreview = preview;
    this.reloadCalibHold = false;
    this.reloadCalibBeat = null;
    this.magCalibBeat = null;
    this.animState = 'reloading';
    this.animT = 0;
    this.magDropped = false;
    this.magInserted = false;
    this.magSeatSfx = false;
    this.clearReloadFlyers();
    if (this.magHeld) {
      this.magHeld.parent?.remove(this.magHeld);
      this.magHeld = null;
      this.magHeldKind = null;
    }
    const mag = this.rigFor(id)?.mag;
    if (mag) mag.visible = true;
    if (this.reloadPair) this.reloadPair.visible = false;
    if (!quiet) {
      if (id === 'mp5') this.audio.play('mp5Reload', { volume: 0.7, rate: 1.18 });
      else if (id === 'ak47') this.audio.play('ak47Reload', { volume: 0.7, rate: 0.82 });
      else this.audio.play('pistolReload', { volume: 0.7, rate: 1 });
    }
  }

  private tryFire(player: PlayerController, worldScene: THREE.Object3D): void {
    const def = this.currentWeapon;
    const a = this.ammo[def.id];

    if (def.requiresPump) {
      if (a.mag <= 0) return;
      a.mag -= 1;
      this.audio.play('shotgun', { volume: 1 });
      this.animState = 'pumping';
      this.animT = 0;
    } else if (isMeleeWeapon(def.id)) {
      this.startPunch(player, worldScene);
      return;
    } else if (isLauncherWeapon(def.id)) {
      if (def.id === 'gl') {
        const total = a.mag + a.reserve;
        if (total <= 0) return;
        const next = total - 1;
        a.mag = Math.min(def.magSize, next);
        a.reserve = next - a.mag;
        this.audio.play('glFire', { volume: 0.78, rate: 0.96 + Math.random() * 0.08 });
        this.animState = 'firing';
        this.launchProjectile(player, 'gl');
      } else {
        if (a.mag <= 0) {
          this.beginReload();
          return;
        }
        a.mag -= 1;
        this.syncRpgRocket();
        const stopFire = this.audio.play('rocketFire', { volume: 0.85, rate: 0.92 + Math.random() * 0.1 });
        this.animState = 'firing';
        this.pendingRpgReload = a.reserve > 0;
        this.launchProjectile(player, 'rpg', stopFire);
      }
    } else if (def.id === 'sawedoff') {
      if (a.mag < 2) {
        this.beginReload();
        return;
      }
      a.mag = 0;
      this.audio.playSawedOff();
      this.animState = 'firing';
      this.pendingSawedReload = true;
    } else {
      if (a.mag <= 0) {
        this.beginReload();
        return;
      }
      a.mag -= 1;
      if (def.id === 'mp5') {
        this.autoShotFlip = !this.autoShotFlip;
        this.audio.play(this.autoShotFlip ? 'mp5' : 'mp5b', {
          volume: 0.62,
          rate: 0.97 + Math.random() * 0.08,
        });
      } else if (def.id === 'ak47') {
        this.autoShotFlip = !this.autoShotFlip;
        this.audio.play(this.autoShotFlip ? 'ak47' : 'ak47b', {
          volume: 0.72,
          rate: 0.94 + Math.random() * 0.08,
        });
      } else if (def.id === 'pistol' && this.pistolSilenced) {
        this.audio.playSilencedPistol();
      } else {
        this.audio.play(Math.random() > 0.5 ? 'pistol' : 'pistol2', {
          volume: 0.85,
          rate: 0.95 + Math.random() * 0.1,
        });
      }
      this.spawnCasing();
      if (def.automatic) this.animState = 'firing';
    }

    this.fireCooldown = def.fireInterval;
    // Snappy visual kick + FOV punch
    this.recoil = 0.65;
    this.kickPitch = def.recoilKick * (0.9 + Math.random() * 0.35);
    this.kickYaw = (Math.random() - 0.5) * def.recoilKick * 0.45;
    this.fovPunch = isShotgunLike(def.id) ? def.id === 'sawedoff' ? 6.2 : 4.5 : def.automatic ? 1.6 : 2.8;
    if (!isLauncherWeapon(def.id)) {
      const flash =
        def.id === 'pistol' && this.pistolSilenced
          ? 0.22
          : def.id === 'sawedoff'
            ? 2.4
            : def.id === 'shotgun'
              ? 1.8
              : def.automatic
                ? 1.1
                : 1.25;
      this.flashMuzzle(flash);
      this.firePellets(player, worldScene, def);
    }
    player.addRecoilPunch(def.recoilKick * (0.35 + Math.random() * 0.15));
  }

  private startPunch(player: PlayerController, worldScene: THREE.Object3D): void {
    if (this.punchTiredT > 0) return;
    if (this.punchComboWindow <= 0) this.punchComboStep = 0;
    const next = nextPunchCombo(this.punchComboStep);
    this.punchStrike = next.id;
    this.punchComboStep = next.nextStep;
    this.punchComboWindow = PUNCH_COMBO_WINDOW;
    this.punchHotT = 2.8;
    const clip = this.punchClips[this.punchStrike];
    this.punchT = 0;
    this.punchFrozenClip = null;
    this.punchFrozenKey = null;
    this.punchPendingHit = true;
    this.punchHitPlayer = player;
    this.worldScene = worldScene;
    this.animState = 'firing';
    this.fireCooldown = punchPlayDuration(clip);
    this.recoil = 0.4;
    this.kickPitch = 0.04;
    this.kickYaw = (Math.random() - 0.5) * 0.03;
    this.audio.play('meleeSwing', {
      volume: 0.22,
      rate: punchSwingRate(this.punchStrike) + (Math.random() - 0.5) * 0.03,
    });
  }

  playPunchPreview(id: PunchClipId): void {
    this.punchFrozenClip = null;
    this.punchFrozenKey = null;
    this.punchPendingHit = false;
    this.punchStrike = id;
    this.punchT = 0;
    this.animState = 'firing';
    this.audio.play('meleeSwing', { volume: 0.2, rate: punchSwingRate(id) });
  }

  freezePunchBeat(clipId: PunchClipId, keyId: PunchKeyId): void {
    const clip = this.punchClips[clipId];
    const key = punchKeyOf(clip, keyId);
    if (!key) return;
    this.punchStrike = clipId;
    this.punchFrozenClip = clipId;
    this.punchFrozenKey = keyId;
    this.punchPendingHit = false;
    this.punchT = key.t * clip.duration;
    this.animState = 'firing';
  }

  clearPunchFreeze(): void {
    this.punchFrozenClip = null;
    this.punchFrozenKey = null;
    this.punchStrike = null;
    this.punchT = 0;
    this.punchPendingHit = false;
    this.animState = 'idle';
  }

  resetPunchClip(id: PunchClipId): void {
    const baked = bakedPunchClip(id);
    const live = this.punchClips[id];
    live.duration = baked.duration;
    for (const key of live.keys) {
      const src = punchKeyOf(baked, key.id);
      if (src) copyPunchKey(key, src);
    }
  }

  resetPunchBeat(clipId: PunchClipId, keyId: PunchKeyId): void {
    const live = punchKeyOf(this.punchClips[clipId], keyId);
    const src = punchKeyOf(bakedPunchClip(clipId), keyId);
    if (live && src) copyPunchKey(live, src);
  }

  punchClip(id: PunchClipId): PunchClip {
    return this.punchClips[id];
  }

  punchKey(clipId: PunchClipId, keyId: PunchKeyId): PunchKey | null {
    return punchKeyOf(this.punchClips[clipId], keyId);
  }

  get punchHoldFrozen(): boolean {
    return this.punchFrozenKey != null;
  }

  get punchFrozenClipId(): PunchClipId | null {
    return this.punchFrozenClip;
  }

  get punchFrozenKeyId(): PunchKeyId | null {
    return this.punchFrozenKey;
  }

  private launchProjectile(player: PlayerController, id: 'gl' | 'rpg', stopFire?: () => void): void {
    if (!this.projectiles) {
      stopFire?.();
      return;
    }
    player.camera.getWorldDirection(this.shotDir);
    // Viewmodel muzzle lives in a separate scene — its world matrix is not the
    // facility. Always spawn from the player camera in the game world.
    this.shotOrigin.copy(player.camera.position).addScaledVector(this.shotDir, 0.78);
    this.shotOrigin.y -= 0.05;
    if (id === 'gl') {
      const poseLoft = lerp(this.glHipRot.x, this.glAdsRot.x, this.adsBlend);
      this.shotRight.set(1, 0, 0).applyQuaternion(player.camera.quaternion);
      this.shotDir.applyAxisAngle(this.shotRight, GL_BARREL_LOFT + poseLoft);
    }
    this.projectiles.spawn(id, this.shotOrigin, this.shotDir, stopFire);
    this.onShot?.({
      origin: this.shotOrigin.clone(),
      direction: this.shotDir.clone(),
      weapon: WEAPONS[id],
      pelletIndex: 0,
    });
  }

  private tryPunchHit(player: PlayerController, worldScene: THREE.Object3D): void {
    player.camera.getWorldDirection(this.shotDir);
    this.shotOrigin.copy(player.camera.position);
    this.impactRay.set(this.shotOrigin, this.shotDir);
    this.impactRay.far = this.shotDir.y > 0.32 ? 2.55 : 1.85;
    const hits = this.impactRay.intersectObject(worldScene, true);
    for (const h of hits) {
      if (isIgnoredHitTarget(h.object)) continue;
      const glass = findDestructibleGlass(h.object);
      if (glass && !glass.userData.destroyed) {
        const n = new THREE.Vector3(0, 0, 1);
        if (h.face) {
          n.copy(h.face.normal).transformDirection(h.object.matrixWorld).normalize();
        }
        this.shatterGlass(glass, h.point, n, this.shotDir, worldScene);
        this.audio.play('meleeHit', { volume: 0.45, rate: 1.15 + Math.random() * 0.15 });
        this.onSmash?.(h.point.clone());
        return;
      }
      if (this.tryBurstToilet(h.object, true)) {
        this.audio.play('meleeHit', { volume: 0.4, rate: 1.2 + Math.random() * 0.15 });
        return;
      }
      if (this.tryBreakCrate(h.object)) return;
      if (this.tryBreakLamp(h.object)) return;
      if (this.tryBreakGrate(h.object)) return;
      if (this.tryBreakAlarm(h.object)) return;
      const strike = this.punchStrike ?? 'jabR';
      const result = this.onEnemyHit?.(
        h.object,
        punchDamage(strike),
        h.point,
        this.shotDir,
        'punch',
        punchStun(strike),
      );
      if (result === 'blocked') {
        this.becomePunchTired();
        return;
      }
      if (result) {
        this.audio.play('meleeHit', { volume: 0.7, rate: 0.9 + Math.random() * 0.2 });
        this.audio.play('hitBody', { volume: 0.52, rate: 0.95 + Math.random() * 0.1 });
        return;
      }
      const n = new THREE.Vector3(0, 1, 0);
      if (h.face) {
        n.copy(h.face.normal).transformDirection(h.object.matrixWorld).normalize();
      }
      this.spawnImpact(h.point, n, this.shotDir, worldScene, false);
      this.audio.playHit(hitSurfaceFromObject(h.object), { volume: 0.4 });
      this.audio.play('meleeHit', { volume: 0.24, rate: 1.05 + Math.random() * 0.12 });
      this.onThud?.(h.point.clone());
      return;
    }
  }

  private becomePunchTired(): void {
    this.punchTiredT = 1.05;
    this.punchStrike = null;
    this.punchT = 0;
    this.punchPendingHit = false;
    this.punchComboStep = 0;
    this.punchComboWindow = 0;
    this.punchHotT = 0;
    this.animState = 'idle';
    this.fireCooldown = 1.05;
    this.audio.play('meleeHit', { volume: 0.38, rate: 1.45 + Math.random() * 0.15 });
    this.audio.play('playerGrunt', { volume: 0.55, rate: 0.92 + Math.random() * 0.12 });
  }

  /** Live melee stays on the calibrated guard / fist pose. Studio still blends via ADS. */
  private punchAds(): number {
    if (this.punchTiredT > 0) return 0;
    if (this.calibFrozen) return this.ads;
    if (this.weaponId === 'punch') return 1;
    return this.ads;
  }

  private applyPunchFists(): void {
    const rig = this.punch;
    if (!rig?.gripR || !rig.gripL) return;
    const ads = this.punchAds();
    let fists = blendStance(ads);
    if (this.punchFrozenClip && this.punchFrozenKey) {
      const key = punchKeyOf(this.punchClips[this.punchFrozenClip], this.punchFrozenKey);
      if (key) fists = applyStance({ right: key.right, left: key.left }, ads);
    } else if (this.punchStrike) {
      const clip = this.punchClips[this.punchStrike];
      const dur = this.punchFrozenKey ? Math.max(0.05, clip.duration) : punchPlayDuration(clip);
      const u = Math.min(1, this.punchT / dur);
      fists = applyStance(samplePunchClip(clip, u), ads);
      if (this.punchPendingHit && this.punchHitPlayer && this.worldScene && u >= punchImpactT(clip)) {
        this.punchPendingHit = false;
        this.tryPunchHit(this.punchHitPlayer, this.worldScene);
      }
    }
    rig.gripR.position.set(fists.right.x, fists.right.y, fists.right.z);
    rig.gripR.rotation.set(fists.right.rx, fists.right.ry, fists.right.rz);
    rig.gripL.position.set(fists.left.x, fists.left.y, fists.left.z);
    rig.gripL.rotation.set(fists.left.rx, fists.left.ry, fists.left.rz);
  }

  private activeRig(): LoadedViewWeapon | null {
    return this.rigFor(this.weaponId);
  }

  private firePellets(player: PlayerController, worldScene: THREE.Object3D, def: WeaponDef): void {
    const spread = lerp(def.hipSpreadDeg, def.adsSpreadDeg, this.ads) * DEG2RAD;
    player.camera.getWorldDirection(this.shotDir);
    this.shotOrigin.copy(player.camera.position);
    const muzzleWorld = this.worldMuzzlePoint();

    // Resolve all pellet hits first — spawning FX mid-loop made later pellets
    // hit smoke/debris instead of the wall (shotgun looked like one hole).
    type PendingHit = {
      dir: THREE.Vector3;
      point: THREE.Vector3;
      normal: THREE.Vector3;
      object: THREE.Object3D | null;
      glass: THREE.Object3D | null;
      glassPoint: THREE.Vector3 | null;
      glassNormal: THREE.Vector3 | null;
      pelletIndex: number;
    };
    const pending: PendingHit[] = [];

    for (let i = 0; i < def.pellets; i++) {
      const dir = this.shotDir.clone();
      if (spread > 0) {
        dir.x += (Math.random() * 2 - 1) * spread;
        dir.y += (Math.random() * 2 - 1) * spread;
        dir.z += (Math.random() * 2 - 1) * spread;
        dir.normalize();
      }

      this.impactRay.set(this.shotOrigin, dir);
      this.impactRay.far = 120;
      const hits = this.impactRay.intersectObject(worldScene, true);
      let glass: THREE.Object3D | null = null;
      let glassHit: THREE.Intersection | undefined;
      let solid: THREE.Intersection | undefined;
      for (const h of hits) {
        if (isIgnoredHitTarget(h.object)) continue;
        const g = findDestructibleGlass(h.object);
        if (g && !g.userData.destroyed) {
          if (!glass) {
            glass = g;
            glassHit = h;
          }
          continue;
        }
        solid = h;
        break;
      }
      const hitEnd = (solid ?? glassHit)?.point.clone() ?? this.shotOrigin.clone().addScaledVector(dir, 90);
      this.spawnShotLine(muzzleWorld, hitEnd, def.id);

      if (glass || solid) {
        const hit = solid ?? glassHit!;
        const n = new THREE.Vector3(0, 1, 0);
        if (hit.face) {
          n.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
        }
        let glassNormal: THREE.Vector3 | null = null;
        if (glassHit?.face) {
          glassNormal = new THREE.Vector3()
            .copy(glassHit.face.normal)
            .transformDirection(glassHit.object.matrixWorld)
            .normalize();
        } else if (glass) {
          glassNormal = n.clone();
        }
        pending.push({
          dir,
          point: hit.point.clone(),
          normal: n,
          object: solid?.object ?? null,
          glass,
          glassPoint: glassHit ? glassHit.point.clone() : null,
          glassNormal,
          pelletIndex: i,
        });
      }
    }

    let hitSfx = false;
    const enemyHits = new Set<THREE.Object3D>();
    for (let i = 0; i < pending.length; i++) {
      const h = pending[i];
      if (h.glass && !h.glass.userData.destroyed && h.glassPoint && h.glassNormal) {
        this.shatterGlass(h.glass, h.glassPoint, h.glassNormal, h.dir, worldScene);
      }
      if (!h.object) continue;

      // Shotgun: stack pellet damage, but only one flesh SFX per volley per mesh tree
      const enemyRoot = findEnemyRoot(h.object);
      if (enemyRoot && this.onEnemyHit) {
        const dmg = def.damage;
        if (this.onEnemyHit(h.object, dmg, h.point, h.dir, def.id)) {
          if (!enemyHits.has(enemyRoot)) {
            enemyHits.add(enemyRoot);
            if (!hitSfx) {
              this.audio.play('hitBody', { volume: 0.52, rate: 0.95 + Math.random() * 0.2 });
              hitSfx = true;
            }
          }
          continue;
        }
      }

      if (this.tryBurstToilet(h.object)) continue;
      if (this.tryBreakCrate(h.object)) continue;
      if (this.tryBreakLamp(h.object)) continue;
      if (this.tryBreakGrate(h.object)) continue;
      if (this.tryBreakAlarm(h.object)) continue;

      // Full juice on first pellet; lighter chips on the rest (shotgun spread)
      const heavy = isShotgunLike(def.id) ? i === 0 : false;
      this.spawnImpact(
        h.point,
        h.normal,
        h.dir,
        worldScene,
        heavy || !isShotgunLike(def.id),
        (0.07 / 0.045) * 0.3,
        isShotgunLike(def.id) && i > 0,
      );
      if (!hitSfx) {
        const surf = hitSurfaceFromObject(h.object);
        this.audio.playHit(surf);
        if (surf === 'metal') this.audio.playRicochet(h.point);
        hitSfx = true;
      }
    }

    this.onShot?.({
      origin: this.shotOrigin.clone(),
      direction: this.shotDir.clone(),
      weapon: def,
      pelletIndex: 0,
      silenced: def.id === 'pistol' && this.pistolSilenced,
    });
  }

  private shatterGlass(
    pane: THREE.Object3D,
    point: THREE.Vector3,
    normal: THREE.Vector3,
    inbound: THREE.Vector3,
    worldScene: THREE.Object3D,
  ): void {
    pane.userData.destroyed = true;
    pane.userData.ignoreHit = true;
    this.audio.playGlassShatter();

    const n = normal.clone().normalize();
    if (n.dot(inbound) > 0) n.negate();
    const push = inbound.clone().normalize();

    const mesh = pane as THREE.Mesh;
    mesh.updateWorldMatrix(true, false);
    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const faceAxes = [
      { axis: new THREE.Vector3(1, 0, 0), len: size.x },
      { axis: new THREE.Vector3(0, 1, 0), len: size.y },
      { axis: new THREE.Vector3(0, 0, 1), len: size.z },
    ].sort((a, b) => b.len - a.len);
    const across = faceAxes[0].axis;
    const along = faceAxes[1].axis;
    const halfW = Math.max(0.12, faceAxes[0].len * 0.48);
    const halfH = Math.max(0.12, faceAxes[1].len * 0.48);

    // Hit flash + expanding shock ring in the glass plane
    const flashMat = new THREE.SpriteMaterial({
      color: 0xe8f8ff,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const flash = new THREE.Sprite(flashMat);
    flash.userData.ignoreHit = true;
    flash.position.copy(point).addScaledVector(n, 0.03);
    flash.scale.setScalar(0.35);
    flash.userData.life = 0.18;
    flash.userData.maxLife = 0.18;
    flash.userData.baseScale = 0.35;
    flash.userData.vel = n.clone().multiplyScalar(0.15);
    flash.userData.baseOpacity = 0.95;
    flash.userData.grow = 4.5;
    worldScene.add(flash);
    this.impactSmoke.push(flash);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.04, 0.1, 28),
      new THREE.MeshBasicMaterial({
        color: 0xc8eeff,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    ring.userData.ignoreHit = true;
    ring.userData.fx = 'ring';
    ring.userData.life = 0.28;
    ring.userData.maxLife = 0.28;
    ring.userData.vel = new THREE.Vector3(0, 0, 0);
    ring.userData.spin = new THREE.Vector3(0, 0, 0);
    ring.position.copy(point).addScaledVector(n, 0.02);
    ring.lookAt(point.clone().add(n));
    worldScene.add(ring);
    this.impactDebris.push(ring);

    // Mist puff through the opening
    if (this.smokeMaps.length) {
      for (let i = 0; i < 2; i++) {
        const map = this.smokeMaps[(Math.random() * this.smokeMaps.length) | 0];
        const mat = new THREE.SpriteMaterial({
          map,
          color: 0xb8d0dc,
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const puff = new THREE.Sprite(mat);
        puff.userData.ignoreHit = true;
        const s0 = 0.18 + Math.random() * 0.1;
        puff.scale.setScalar(s0);
        puff.position.copy(point).addScaledVector(n, 0.05 + i * 0.04);
        puff.userData.life = 0.4 + Math.random() * 0.15;
        puff.userData.maxLife = puff.userData.life as number;
        puff.userData.baseScale = s0;
        puff.userData.baseOpacity = 0.22;
        puff.userData.grow = 2.4;
        puff.userData.vel = push
          .clone()
          .multiplyScalar(0.6 + Math.random() * 0.5)
          .addScaledVector(n, (Math.random() - 0.3) * 0.8);
        worldScene.add(puff);
        this.impactSmoke.push(puff);
      }
    }

    const spawnShard = (
      pos: THREE.Vector3,
      large: boolean,
      fromImpact: boolean,
    ): void => {
      const icy = Math.random();
      const color = icy > 0.66 ? 0xf2fbff : icy > 0.33 ? 0xa8d4e8 : 0x6a9bb0;
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: large ? 0.82 : 0.7,
        side: THREE.DoubleSide,
      });
      const shard = new THREE.Mesh(createGlassShardGeometry(large), mat);
      shard.userData.ignoreHit = true;
      shard.userData.fx = 'glass';
      shard.position.copy(pos);

      // Face the pane, then tumble
      shard.lookAt(pos.clone().add(n));
      shard.rotateZ(Math.random() * Math.PI * 2);

      const away = pos.clone().sub(point);
      if (away.lengthSq() < 1e-5) {
        away.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5);
      }
      away.normalize();

      const blast = fromImpact ? 6.5 + Math.random() * 4 : 2.2 + Math.random() * 2.8;
      const vel = away.multiplyScalar(blast);
      vel.addScaledVector(push, 3.2 + Math.random() * 3.5);
      vel.addScaledVector(n, (Math.random() - 0.35) * 3.5);
      vel.y += 1.2 + Math.random() * 2.4;
      // Big plates hang a beat then drop; chips scream out
      if (large && !fromImpact) vel.multiplyScalar(0.55);

      shard.userData.vel = vel;
      shard.userData.life = large ? 0.85 + Math.random() * 0.55 : 0.4 + Math.random() * 0.35;
      shard.userData.spin = new THREE.Vector3(
        (Math.random() - 0.5) * (large ? 18 : 36),
        (Math.random() - 0.5) * (large ? 18 : 36),
        (Math.random() - 0.5) * (large ? 22 : 40),
      );
      worldScene.add(shard);
      this.impactDebris.push(shard);
    };

    // Large plates seeded across the pane — whole window comes apart
    const cols = 3;
    const rows = 4;
    for (let iy = 0; iy < rows; iy++) {
      for (let iz = 0; iz < cols; iz++) {
        const u = (iz + 0.5) / cols - 0.5;
        const v = (iy + 0.5) / rows - 0.5;
        const pos = center
          .clone()
          .addScaledVector(n, (Math.random() - 0.5) * 0.04)
          .addScaledVector(along, v * halfH * 2 + (Math.random() - 0.5) * 0.08)
          .addScaledVector(across, u * halfW * 2 + (Math.random() - 0.5) * 0.08);
        const nearHit = pos.distanceTo(point) < 0.35;
        spawnShard(pos, true, nearHit);
      }
    }

    // Fine chips + sparkles from the bullet hole
    for (let i = 0; i < 14; i++) {
      const pos = point
        .clone()
        .add(
          new THREE.Vector3(
            (Math.random() - 0.5) * 0.12,
            (Math.random() - 0.5) * 0.2,
            (Math.random() - 0.5) * 0.2,
          ),
        );
      spawnShard(pos, false, true);
    }
    for (let i = 0; i < 8; i++) {
      const spark = new THREE.Mesh(
        this.sparkGeo,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 1,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      spark.userData.ignoreHit = true;
      spark.userData.fx = 'spark';
      spark.position.copy(point);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 8,
        Math.random() * 5 + 1,
        (Math.random() - 0.5) * 8,
      );
      vel.addScaledVector(push, 4 + Math.random() * 4);
      vel.addScaledVector(n, (Math.random() - 0.5) * 3);
      spark.userData.vel = vel;
      spark.userData.life = 0.18 + Math.random() * 0.16;
      spark.userData.spin = new THREE.Vector3(0, 0, 0);
      worldScene.add(spark);
      this.impactDebris.push(spark);
    }

    const hide = pane.userData.hideOnShatter as THREE.Object3D[] | undefined;
    if (hide) {
      for (const o of hide) o.visible = false;
    }

    // Drop the pane — Reflector has its own RT; window panes share a material.
    pane.traverse((o) => {
      const r = o as THREE.Mesh & { dispose?: () => void; getRenderTarget?: () => unknown };
      if (typeof r.dispose === 'function' && typeof r.getRenderTarget === 'function') {
        r.dispose();
        r.geometry.dispose();
      }
    });
    pane.parent?.remove(pane);
    if (mesh.isMesh && mesh.geometry) mesh.geometry.dispose();
  }

  private worldMuzzlePoint(): THREE.Vector3 {
    const local = this.muzzlePoseLocal();
    this.pose.updateWorldMatrix(true, false);
    this.pose.localToWorld(local);
    if (this.playerCamera) this.playerCamera.localToWorld(local);
    return local;
  }

  private spawnShotLine(from: THREE.Vector3, to: THREE.Vector3, id: WeaponId): void {
    if (!this.shotLines || isMeleeWeapon(id) || isLauncherWeapon(id)) return;
    const kind: ShotLineKind =
      id === 'pistol' && this.pistolSilenced
        ? 'silenced'
        : isShotgunLike(id)
          ? 'shotgun'
          : id === 'mp5' || id === 'ak47'
            ? 'rifle'
            : 'pistol';
    this.shotLines.spawn(from, to, kind);
  }

  private spawnImpact(
    point: THREE.Vector3,
    normal: THREE.Vector3,
    inbound: THREE.Vector3,
    worldScene: THREE.Object3D,
    heavy = false,
    decalScale = 1,
    extraPellet = false,
  ): void {
    const n = normal.clone().normalize();
    if (n.dot(inbound) > 0) n.negate();

    const decal = this.acquireDecal();
    decal.scale.setScalar(decalScale);
    decal.visible = true;
    decal.userData.life = 8;
    decal.position.copy(point).addScaledVector(n, 0.012);
    decal.lookAt(this.decalLook.copy(point).add(n));
    worldScene.add(decal);
    this.liveDecals.push(decal);

    // Dust / smoke puff on the mark
    if (this.smokeSpriteMat && this.smokeMaps.length) {
      const map = this.smokeMaps[(Math.random() * this.smokeMaps.length) | 0];
      const mat = this.smokeSpriteMat.clone();
      mat.map = map;
      mat.color.setHex(0x8a8478);
      mat.opacity = 0.28;
      const puff = new THREE.Sprite(mat);
      puff.userData.ignoreHit = true;
      const s0 = heavy ? 0.14 : 0.07;
      puff.scale.setScalar(s0);
      puff.position.copy(point).addScaledVector(n, 0.04);
      puff.userData.life = 0.35 + Math.random() * 0.15;
      puff.userData.maxLife = puff.userData.life as number;
      puff.userData.baseScale = s0;
      puff.userData.vel = n.clone().multiplyScalar(0.35 + Math.random() * 0.25);
      worldScene.add(puff);
      this.impactSmoke.push(puff);
    }

    // Chips + sparks bouncing off the surface
    const ricochet = inbound.clone().reflect(n);
    const count = extraPellet ? 1 : heavy ? 6 : 3;
    for (let i = 0; i < count; i++) {
      const isSpark = Math.random() > 0.45;
      const chip = this.acquireChip(isSpark);
      const mat = chip.material as THREE.MeshBasicMaterial;
      mat.opacity = isSpark ? 0.95 : 0.9;
      chip.visible = true;
      chip.userData.ignoreHit = true;
      chip.position.copy(point).addScaledVector(n, 0.02);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 2.2,
        Math.random() * 1.8 + 0.4,
        (Math.random() - 0.5) * 2.2,
      );
      vel.addScaledVector(n, 1.6 + Math.random() * 1.4);
      vel.addScaledVector(ricochet, 0.8 + Math.random() * 0.9);
      chip.userData.vel = vel;
      chip.userData.life = 0.35 + Math.random() * 0.35;
      chip.userData.spin = new THREE.Vector3(
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22,
      );
      chip.userData.isSpark = isSpark;
      worldScene.add(chip);
      this.impactDebris.push(chip);
    }
  }

  private initImpactPools(): void {
    for (let i = 0; i < 24; i++) {
      const d = new THREE.Mesh(this.decalGeo, this.decalMat);
      d.userData.ignoreHit = true;
      d.userData.pooled = true;
      d.visible = false;
      this.decalPool.push(d);
    }
    for (let i = 0; i < 16; i++) {
      const spark = new THREE.Mesh(this.sparkGeo, this.sparkMat.clone());
      spark.userData.ignoreHit = true;
      spark.userData.pooled = true;
      spark.userData.isSpark = true;
      spark.visible = false;
      this.sparkPool.push(spark);
      const chip = new THREE.Mesh(this.debrisGeo, this.chipMat.clone());
      chip.userData.ignoreHit = true;
      chip.userData.pooled = true;
      chip.userData.isSpark = false;
      chip.visible = false;
      this.chipPool.push(chip);
    }
  }

  private acquireDecal(): THREE.Mesh {
    const d = this.decalPool.pop();
    if (d) return d;
    const oldest = this.liveDecals.shift();
    if (oldest) {
      oldest.parent?.remove(oldest);
      return oldest;
    }
    const extra = new THREE.Mesh(this.decalGeo, this.decalMat);
    extra.userData.ignoreHit = true;
    extra.userData.pooled = true;
    return extra;
  }

  private releaseDecal(d: THREE.Mesh): void {
    d.visible = false;
    d.parent?.remove(d);
    this.decalPool.push(d);
  }

  private acquireChip(isSpark: boolean): THREE.Mesh {
    const pool = isSpark ? this.sparkPool : this.chipPool;
    const ready = pool.pop();
    if (ready) return ready;
    for (let i = 0; i < this.impactDebris.length; i++) {
      const c = this.impactDebris[i]!;
      if (c.userData.pooled && !!c.userData.isSpark === isSpark) {
        this.impactDebris.splice(i, 1);
        c.parent?.remove(c);
        return c;
      }
    }
    const mesh = new THREE.Mesh(
      isSpark ? this.sparkGeo : this.debrisGeo,
      (isSpark ? this.sparkMat : this.chipMat).clone(),
    );
    mesh.userData.ignoreHit = true;
    mesh.userData.pooled = true;
    mesh.userData.isSpark = isSpark;
    return mesh;
  }

  private releaseChip(c: THREE.Mesh): void {
    c.visible = false;
    c.parent?.remove(c);
    (c.userData.isSpark ? this.sparkPool : this.chipPool).push(c);
  }

  private updateImpactFX(dt: number): void {
    for (let i = this.liveDecals.length - 1; i >= 0; i--) {
      const d = this.liveDecals[i]!;
      d.userData.life -= dt;
      if ((d.userData.life as number) <= 0) {
        this.releaseDecal(d);
        this.liveDecals.splice(i, 1);
      }
    }

    for (let i = this.impactSmoke.length - 1; i >= 0; i--) {
      const p = this.impactSmoke[i];
      const vel = p.userData.vel as THREE.Vector3;
      p.position.addScaledVector(vel, dt);
      vel.multiplyScalar(0.92);
      p.userData.life -= dt;
      const life = p.userData.life as number;
      const maxLife = p.userData.maxLife as number;
      const t = Math.max(0, life / maxLife);
      const base = p.userData.baseScale as number;
      const grow = (p.userData.grow as number | undefined) ?? 2.2;
      const s = base * (1.15 + (1 - t) * grow);
      p.scale.setScalar(s);
      const mat = p.material as THREE.SpriteMaterial;
      const baseOp = (p.userData.baseOpacity as number | undefined) ?? 0.3;
      mat.opacity = baseOp * t * t;
      if (life <= 0) {
        this.worldScene?.remove(p);
        mat.dispose();
        this.impactSmoke.splice(i, 1);
      }
    }

    for (let i = this.impactDebris.length - 1; i >= 0; i--) {
      const c = this.impactDebris[i];
      const fx = c.userData.fx as string | undefined;
      const vel = c.userData.vel as THREE.Vector3;
      const mat = c.material as THREE.MeshBasicMaterial;

      if (fx === 'ring') {
        c.userData.life -= dt;
        const life = c.userData.life as number;
        const maxLife = c.userData.maxLife as number;
        const t = Math.max(0, life / maxLife);
        const grow = 1 + (1 - t) * 7;
        c.scale.setScalar(grow);
        mat.opacity = 0.85 * t * t;
        if (life <= 0) {
          this.worldScene?.remove(c);
          if (!c.userData.pooled) {
            c.geometry.dispose();
            mat.dispose();
          }
          this.impactDebris.splice(i, 1);
        }
        continue;
      }

      const grav = fx === 'spark' ? 6 : fx === 'glass' ? 11 : 14;
      vel.y -= grav * dt;
      if (fx === 'spark') vel.multiplyScalar(1 - 1.8 * dt);
      c.position.addScaledVector(vel, dt);
      const spin = c.userData.spin as THREE.Vector3;
      c.rotation.x += spin.x * dt;
      c.rotation.y += spin.y * dt;
      c.rotation.z += spin.z * dt;
      // Soft bounce off floor
      if (c.position.y < this.groundY + 0.03 && vel.y < 0) {
        c.position.y = this.groundY + 0.03;
        vel.y *= -0.35;
        vel.x *= 0.6;
        vel.z *= 0.6;
        if (fx === 'glass') spin.multiplyScalar(0.5);
      }
      c.userData.life -= dt;
      const life = c.userData.life as number;
      if (fx === 'spark') {
        mat.opacity = Math.max(0, life * 5);
        c.scale.setScalar(0.6 + life * 4);
      } else if (fx === 'glass') {
        mat.opacity = Math.max(0, Math.min(0.9, life * 1.35));
      } else {
        mat.opacity = Math.max(0, life * 2.2);
      }
      if (life <= 0) {
        if (c.userData.pooled) {
          this.releaseChip(c);
        } else {
          this.worldScene?.remove(c);
          if (c.geometry !== this.debrisGeo && c.geometry !== this.sparkGeo) {
            c.geometry.dispose();
          }
          mat.dispose();
        }
        this.impactDebris.splice(i, 1);
      }
    }
  }

  private flashMuzzle(power = 1): void {
    const mpos = this.muzzlePoseLocal();
    this.muzzleFlashGroup.position.copy(mpos);
    this.muzzleLight.position.copy(mpos);
    this.muzzleFlashGroup.rotation.set(0, 0, Math.random() * Math.PI);
    this.muzzleFlashGroup.scale.setScalar((0.5 + Math.random() * 0.18) * power);
    this.flashT = 0.05 + 0.03 * power;
    this.muzzleFlashGroup.visible = true;
    this.muzzleLight.intensity = 20 * power;
    this.pulseWorldMuzzleGlow(mpos, power);
    this.spawnMuzzleSmoke(mpos, power);
  }

  private pulseWorldMuzzleGlow(poseLocal: THREE.Vector3, power: number): void {
    if (!this.worldScene || !this.playerCamera) return;
    if (this.worldMuzzleLight.parent !== this.worldScene) {
      this.worldScene.add(this.worldMuzzleLight);
    }
    const worldPos = poseLocal.clone();
    this.pose.localToWorld(worldPos);
    this.playerCamera.localToWorld(worldPos);
    this.worldMuzzleLight.position.copy(worldPos);
    this.worldGlowPeak = (isShotgunLike(this.weaponId) ? 32 : 22) * power;
    this.worldMuzzleLight.intensity = this.worldGlowPeak;
    this.worldMuzzleLight.distance = isShotgunLike(this.weaponId) ? 14 : 11;
    this.worldGlowMaxT = 0.2 + 0.08 * power;
    this.worldGlowT = this.worldGlowMaxT;
  }

  private spawnMuzzleSmoke(poseLocal: THREE.Vector3, _power: number): void {
    if (!this.smokeSpriteMat || !this.smokeMaps.length) return;
    const count = isShotgunLike(this.weaponId) ? 2 : 1;
    for (let i = 0; i < count; i++) {
      const map = this.smokeMaps[(Math.random() * this.smokeMaps.length) | 0];
      // Kenney smoke PNGs are white-on-black — additive hides the black plate
      const mat = this.smokeSpriteMat.clone();
      mat.map = map;
      mat.color.setHex(0x6a655c);
      mat.opacity = 0.11 + Math.random() * 0.05;
      mat.depthTest = true;
      const puff = new THREE.Sprite(mat);
      puff.position.copy(poseLocal);
      puff.position.x += (Math.random() - 0.5) * 0.015;
      puff.position.y += (Math.random() - 0.5) * 0.012;
      puff.position.z -= 0.015 + Math.random() * 0.02;
      const s = 0.04 + Math.random() * 0.025;
      puff.scale.set(s, s, 1);
      puff.material.rotation = Math.random() * Math.PI * 2;
      puff.userData.vel = new THREE.Vector3(
        (Math.random() - 0.5) * 0.04,
        0.035 + Math.random() * 0.04,
        -0.025 - Math.random() * 0.03,
      );
      puff.userData.life = 0.28 + Math.random() * 0.18;
      puff.userData.maxLife = puff.userData.life as number;
      puff.userData.baseScale = s;
      puff.userData.grow = 0.9 + Math.random() * 0.4;
      puff.userData.baseOpacity = mat.opacity;
      this.pose.add(puff);
      this.smokePuffs.push(puff);
    }
  }

  private updateSmoke(dt: number): void {
    for (let i = this.smokePuffs.length - 1; i >= 0; i--) {
      const p = this.smokePuffs[i];
      const vel = p.userData.vel as THREE.Vector3;
      vel.y += 0.08 * dt;
      p.position.addScaledVector(vel, dt);
      const maxLife = p.userData.maxLife as number;
      p.userData.life -= dt;
      const life = p.userData.life as number;
      const t = Math.max(0, life / maxLife);
      const base = p.userData.baseScale as number;
      const grow = p.userData.grow as number;
      const age = 1 - t;
      const s = base * (1 + age * grow);
      p.scale.set(s, s, 1);
      const mat = p.material as THREE.SpriteMaterial;
      mat.opacity = (p.userData.baseOpacity as number) * t * t;
      if (life <= 0) {
        this.pose.remove(p);
        mat.dispose();
        this.smokePuffs.splice(i, 1);
      }
    }
  }

  private updateFlash(dt: number): void {
    if (this.worldGlowT > 0) {
      this.worldGlowT -= dt;
      const g = Math.max(0, this.worldGlowT / this.worldGlowMaxT);
      this.worldMuzzleLight.intensity = this.worldGlowPeak * Math.pow(g, 0.45);
      // Re-anchor to current muzzle while glowing so it tracks the gun briefly
      if (g > 0 && this.playerCamera && this.worldMuzzleLight.parent) {
        const tip = this.muzzleFlashGroup.position.clone();
        this.pose.localToWorld(tip);
        this.playerCamera.localToWorld(tip);
        this.worldMuzzleLight.position.copy(tip);
      }
      if (this.worldGlowT <= 0) this.worldMuzzleLight.intensity = 0;
    }

    if (this.flashT <= 0) {
      this.muzzleFlashGroup.visible = false;
      this.muzzleLight.intensity = 0;
      return;
    }
    this.flashT -= dt;
    const a = Math.max(0, this.flashT / 0.08);
    const core = this.muzzleFlashCore.material as THREE.MeshBasicMaterial;
    const spike = this.muzzleFlashSpike.material as THREE.MeshBasicMaterial;
    const sprite = this.muzzleFlashSprite.material as THREE.MeshBasicMaterial;
    core.opacity = Math.min(0.78, a * 0.95);
    spike.opacity = Math.min(0.58, a * 0.75);
    sprite.opacity = Math.min(0.65, a * 0.82);
    this.muzzleLight.intensity = 22 * a;
    this.muzzleFlashGroup.scale.setScalar((0.5 + (1 - a) * 0.35) * (0.7 + Math.random() * 0.15));
    this.muzzleFlashSprite.rotation.z = this.muzzleFlashGroup.rotation.z;
    this.muzzleFlashGroup.visible = a > 0.02;
  }

  private muzzlePoseLocal(): THREE.Vector3 {
    if (!this.procMode) {
      const fx = this.fxFor(this.weaponId);
      if (fx) {
        return fx.muzzleHip.clone().lerp(fx.muzzleAds, this.ads);
      }
    }
    const rig = this.activeRig();
    const mpos = new THREE.Vector3(0.04, -0.04, -0.38);
    if (rig) {
      this.pose.updateWorldMatrix(true, true);
      rig.root.updateWorldMatrix(true, true);
      const tip = new THREE.Vector3();
      const tipNode =
        this.weaponId === 'pistol' && this.pistolSilenced
          ? (rig.silencer?.getObjectByName('silencer_tip') ?? rig.muzzle)
          : rig.muzzle;
      tipNode.getWorldPosition(tip);
      this.pose.worldToLocal(tip);
      if (tip.z < -0.08) {
        mpos.copy(tip);
        mpos.z -= 0.025;
      }
    }
    if (!this.procMode && this.weaponId === 'pistol') {
      const hip = 1 - this.ads;
      mpos.x += 0.028 * hip + 0.024 * this.ads;
      mpos.y += 0.012 * hip + 0.014 * this.ads;
    } else if (!this.procMode && this.weaponId === 'shotgun') {
      const hip = 1 - this.ads;
      mpos.x -= 0.04 * hip + 0.045 * this.ads;
      mpos.y += 0.016 * this.ads;
    }
    return mpos;
  }

  private casingPoseLocal(): THREE.Vector3 {
    const rig = this.activeRig();
    if (rig?.ejectPort) {
      this.pose.updateWorldMatrix(true, true);
      const tip = new THREE.Vector3();
      rig.ejectPort.getWorldPosition(tip);
      this.pose.worldToLocal(tip);
      return tip;
    }
    const fx = this.fxFor(this.weaponId);
    if (fx) {
      return fx.casingHip.clone().lerp(fx.casingAds, this.ads);
    }
    const ejectLocal = new THREE.Vector3(0.05, -0.08, -0.26);
    if (this.pistol && this.weaponId === 'pistol') {
      const tip = new THREE.Vector3();
      this.pistol.muzzle.getWorldPosition(tip);
      ejectLocal.set(tip.x + 0.045, tip.y + 0.01, tip.z + 0.1);
      this.pose.worldToLocal(ejectLocal);
    }
    return ejectLocal;
  }

  private spawnCasing(): void {
    const pumpShell = this.weaponId === 'shotgun';
    const casing: THREE.Object3D = pumpShell
      ? makePumpCasing()
      : new THREE.Mesh(this.casingGeo, this.casingMat);
    if (!pumpShell) (casing as THREE.Mesh).rotation.z = Math.PI / 2;
    else casing.rotation.y = Math.PI / 2;
    casing.castShadow = false;
    casing.frustumCulled = false;

    const poseLocal = this.casingPoseLocal();
    const world = this.worldScene;
    const cam = this.playerCamera;

    if (!this.calibFrozen && world && cam) {
      const worldPos = poseLocal.clone();
      this.pose.updateWorldMatrix(true, false);
      this.pose.localToWorld(worldPos);
      cam.localToWorld(worldPos);
      const velLocal = pumpShell
        ? new THREE.Vector3(
            2.6 + Math.random() * 1.2,
            1.6 + Math.random() * 0.9,
            0.2 + Math.random() * 0.5,
          )
        : new THREE.Vector3(
            2.2 + Math.random() * 1.2,
            1.4 + Math.random() * 0.9,
            -0.2 + Math.random() * 0.6,
          );
      const velWorld = velLocal.applyQuaternion(cam.quaternion);
      casing.position.copy(worldPos);
      casing.userData.vel = velWorld;
      casing.userData.life = 5.5;
      casing.userData.floorY = this.groundY;
      casing.userData.spin = new THREE.Vector3(
        (Math.random() - 0.5) * 18,
        (Math.random() - 0.5) * 18,
        (Math.random() - 0.5) * 18,
      );
      world.add(casing);
      this.worldCasings.push(casing);
    } else {
      const port = this.activeRig()?.ejectPort;
      if (port) {
        this.pose.updateWorldMatrix(true, true);
        port.getWorldPosition(this.reloadTmpV);
        casing.position.copy(this.reloadTmpV);
      } else {
        casing.position.copy(poseLocal);
      }
      this.pose.getWorldQuaternion(this.reloadTmpQ);
      const vel = pumpShell
        ? new THREE.Vector3(1.9 + Math.random() * 0.6, 1.1 + Math.random() * 0.4, 0.25)
        : new THREE.Vector3(1.8 + Math.random(), 1.2 + Math.random(), 0.4);
      vel.applyQuaternion(this.reloadTmpQ);
      casing.userData.vel = vel;
      casing.userData.life = 1.5;
      casing.userData.spin = new THREE.Vector3(
        (Math.random() - 0.5) * 14,
        (Math.random() - 0.5) * 14,
        (Math.random() - 0.5) * 14,
      );
      this.casings.add(casing);
    }

    this.audio.play('casing', { volume: 0.3, rate: 0.9 + Math.random() * 0.2 });
  }

  private disposeCasing(obj: THREE.Object3D): void {
    obj.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (mesh.geometry !== this.casingGeo && mesh.geometry !== this.debrisGeo && mesh.geometry !== this.sparkGeo) {
        mesh.geometry.dispose();
      }
      const mat = mesh.material;
      const mats = Array.isArray(mat) ? mat : [mat];
      for (const m of mats) {
        if (m && m !== this.casingMat) m.dispose();
      }
    });
  }

  private updateCasings(dt: number): void {
    // Legacy viewmodel-space casings
    for (let i = this.casings.children.length - 1; i >= 0; i--) {
      const c = this.casings.children[i] as THREE.Mesh;
      const vel = c.userData.vel as THREE.Vector3;
      vel.y -= 6 * dt;
      c.position.addScaledVector(vel, dt);
      const spin = c.userData.spin as THREE.Vector3 | undefined;
      if (spin) {
        c.rotation.x += spin.x * dt;
        c.rotation.y += spin.y * dt;
        c.rotation.z += spin.z * dt;
      } else {
        c.rotation.x += dt * 10;
      }
      c.userData.life -= dt;
      if (c.userData.life <= 0) {
        this.casings.remove(c);
        this.disposeCasing(c);
      }
    }

    // World casings — visible in the level
    for (let i = this.worldCasings.length - 1; i >= 0; i--) {
      const c = this.worldCasings[i]!;
      const vel = c.userData.vel as THREE.Vector3;
      vel.y -= 12 * dt;
      c.position.addScaledVector(vel, dt);
      const spin = c.userData.spin as THREE.Vector3 | undefined;
      if (spin) {
        c.rotation.x += spin.x * dt;
        c.rotation.y += spin.y * dt;
        c.rotation.z += spin.z * dt;
      } else {
        c.rotation.x += dt * 12;
      }
      // Soft settle near the floor the player was on when it ejected
      const floor = (c.userData.floorY as number | undefined) ?? this.groundY;
      if (c.position.y < floor + 0.04 && vel.y < 0) {
        c.position.y = floor + 0.04;
        vel.y *= -0.25;
        vel.x *= 0.55;
        vel.z *= 0.55;
        if (Math.abs(vel.y) < 0.35) vel.set(0, 0, 0);
      }
      c.userData.life -= dt;
      if (c.userData.life <= 0) {
        this.worldScene?.remove(c);
        this.disposeCasing(c);
        this.worldCasings.splice(i, 1);
      }
    }
  }

  private updateAnim(dt: number): void {
    const def = this.currentWeapon;

    if (this.animState === 'switching') {
      this.animT += dt;
      if (!this.switchSwapped && this.animT >= this.switchDownDur && this.switchTarget) {
        this.weaponId = this.switchTarget;
        this.switchSwapped = true;
        this.applyWeaponVisibility();
        if (this.weaponId === 'pistol') this.audio.play('equipPistol', { volume: 0.72, rate: 0.98 + Math.random() * 0.04 });
      }
      if (this.animT >= this.switchDownDur + this.switchUpDur) {
        this.animState = 'idle';
        this.animT = 0;
        this.switchTarget = null;
        this.switchSwapped = false;
        const next = this.pendingWeapon;
        this.pendingWeapon = null;
        if (next && next !== this.weaponId) this.switchWeapon(next);
      }
      return;
    }

    if (this.animState === 'reloading') {
      if (!this.reloadCalibHold) this.animT += dt;
      if (def.id === 'rpg' && !this.rpgLoadClick && this.animT / def.reloadTime >= 0.38) {
        this.rpgLoadClick = true;
        this.audio.play('switchClick', { volume: 0.55, rate: 0.82 });
      }
      if (!this.reloadCalibHold && this.animT / def.reloadTime >= 1) {
        if (!this.reloadPreview) {
          const a = this.ammo[def.id];
          const need = def.magSize - a.mag;
          const take = Math.min(need, a.reserve);
          a.mag += take;
          a.reserve -= take;
        }
        this.reloadPreview = false;
        this.animState = 'idle';
        this.animT = 0;
        this.resetDoubleBarrelIdle();
        this.resetMagReloadIdle();
      }
    }

    if (this.animState === 'firing') {
      if (this.punchStrike && !this.punchFrozenKey) {
        this.punchT += dt;
        const dur = punchPlayDuration(this.punchClips[this.punchStrike]);
        if (this.punchT < dur) return;
        this.punchStrike = null;
        this.punchT = 0;
        this.punchPendingHit = false;
      }
      this.animState = 'idle';
      if (this.pendingSawedReload && this.weaponId === 'sawedoff') {
        this.pendingSawedReload = false;
        this.beginReload();
      } else {
        this.pendingSawedReload = false;
      }
      if (this.pendingRpgReload && this.weaponId === 'rpg') {
        this.pendingRpgReload = false;
        this.beginReload();
      } else {
        this.pendingRpgReload = false;
      }
    }

    if (this.animState === 'pumping') {
      this.animT += dt;
      const t = Math.min(1, this.animT / def.pumpTime);
      const pull = 0.3;
      const hold = 0.16;
      if (t < pull) {
        const u = t / pull;
        this.pumpAmount = 1 - (1 - u) * (1 - u) * (1 - u);
      } else if (t < pull + hold) {
        this.pumpAmount = 1;
      } else {
        const u = (t - pull - hold) / (1 - pull - hold);
        this.pumpAmount = (1 - u) * (1 - u);
      }
      if (t >= pull && t < pull + dt / def.pumpTime) {
        this.audio.play('shotgunPump', { volume: 0.92, rate: 0.92 });
        this.spawnCasing();
      }
      if (t >= 1) {
        this.pumpAmount = 0;
        this.animState = 'idle';
        this.animT = 0;
      }
    }
  }

  private updatePose(player: PlayerController, dt: number): void {
    const def = this.currentWeapon;
    const breathY = Math.sin(this.breathPhase) * 0.003;
    const breathX = Math.cos(this.breathPhase * 0.7) * 0.002;

    const bobY = Math.sin(player.bobPhase) * 0.012 * player.bobStrength;
    const bobX = Math.cos(player.bobPhase * 0.5) * 0.008 * player.bobStrength;
    this.bob.position.x = damp(this.bob.position.x, bobX + breathX, 16, dt);
    this.bob.position.y = damp(this.bob.position.y, bobY + breathY, 16, dt);

    this.sway.rotation.y = Math.sin(this.breathPhase * 0.5) * 0.012 * (1 - this.ads * 0.9);
    this.sway.rotation.x = Math.cos(this.breathPhase * 0.4) * 0.008 * (1 - this.ads * 0.9);

    if (this.calibFrozen) {
      this.bob.position.set(0, 0, 0);
      this.sway.rotation.set(0, 0, 0);
      const { hipPos, hipRot, adsPos, adsRot } = this.poseFor(this.weaponId);
      this.pose.position.lerpVectors(hipPos, adsPos, this.ads);
      this.pose.rotation.set(
        lerp(hipRot.x, adsRot.x, this.ads),
        lerp(hipRot.y, adsRot.y, this.ads),
        lerp(hipRot.z, adsRot.z, this.ads),
      );
      if (this.animState === 'reloading' && this.weaponId === 'sawedoff') {
        this.applyReloadGunSample(
          this.pose.position,
          this.pose.rotation,
          this.animT / WEAPONS.sawedoff.reloadTime,
        );
      }
      if (this.animState === 'reloading' && isMagReloadWeapon(this.weaponId)) {
        this.applyMagReloadGunSample(
          this.pose.position,
          this.pose.rotation,
          this.animT / WEAPONS[this.weaponId].reloadTime,
        );
      }
      if (this.animState === 'pumping' && this.weaponId === 'shotgun') {
        this.pose.position.z += this.pumpAmount * 0.03;
        this.pose.position.y -= this.pumpAmount * 0.018;
        this.pose.rotation.x -= this.pumpAmount * 0.08;
      }
      return;
    }

    const { hipPos, hipRot, adsPos, adsRot } = this.poseFor(this.weaponId);
    // One-handed carry: gun tucked right so left sprint arm can pump freely
    const sprintPos = new THREE.Vector3(0.22, -0.38, -0.28);
    const sprintRot = new THREE.Euler(0.65, 0.55, 0.28);
    // Slide: keep weapon ready to fire (hip-ish), slightly lowered
    const slidePos = new THREE.Vector3(0.04, -0.16, -0.3);
    const slideRot = new THREE.Euler(0.04, 0.04, 0.0);

    let targetPos = hipPos.clone().lerp(adsPos, this.ads);
    let targetRotX = lerp(hipRot.x, adsRot.x, this.ads);
    let targetRotY = lerp(hipRot.y, adsRot.y, this.ads);
    let targetRotZ = lerp(hipRot.z, adsRot.z, this.ads);

    const sprintW = this.sprintBlend * (1 - this.ads) * (1 - player.slideBlend);
    targetPos.lerp(sprintPos, sprintW);
    targetRotX = lerp(targetRotX, sprintRot.x, sprintW);
    targetRotY = lerp(targetRotY, sprintRot.y, sprintW);
    targetRotZ = lerp(targetRotZ, sprintRot.z, sprintW);

    targetPos.lerp(slidePos, player.slideBlend);
    targetRotX = lerp(targetRotX, slideRot.x, player.slideBlend);
    targetRotY = lerp(targetRotY, slideRot.y, player.slideBlend);
    targetRotZ = lerp(targetRotZ, slideRot.z, player.slideBlend);

    // Recoil: light kick up + back (viewmodel only). Autos barely leave the pocket.
    const backScale = def.id === 'mp5' || def.id === 'ak47' ? 0.06 : 0.75;
    const liftScale = def.id === 'mp5' || def.id === 'ak47' ? 0.05 : 0.22;
    targetPos.z += this.recoil * def.recoilKick * backScale;
    targetPos.y += this.recoil * def.recoilKick * liftScale;
    // Positive pose pitch = muzzle up (matches hip aim convention)
    targetRotX += this.kickPitch * 2.6 + this.recoil * def.recoilKick * 1.6;
    targetRotY += this.kickYaw * 1.4;
    targetRotZ += this.kickYaw * 0.9;

    if (this.animState === 'reloading') {
      const t = this.animT / def.reloadTime;
      if (def.id === 'sawedoff') {
        const g = this.sampleReload(t).gun;
        targetPos.x += g.x;
        targetPos.y += g.y;
        targetPos.z += g.z;
        targetRotX += g.pitch;
        targetRotY += g.yaw;
        targetRotZ += g.roll;
      } else if (isMagReloadWeapon(def.id)) {
        const g = this.sampleMagReload(t).gun;
        targetPos.x += g.x;
        targetPos.y += g.y;
        targetPos.z += g.z;
        targetRotX += g.pitch;
        targetRotY += g.yaw;
        targetRotZ += g.roll;
      } else if (def.id === 'rpg') {
        const dip = Math.sin(Math.min(1, t) * Math.PI);
        targetPos.x -= dip * 0.18;
        targetPos.y -= dip * 0.3;
        targetRotX += dip * 0.16;
        targetRotZ += dip * 0.46;
      } else {
        const dip = Math.sin(Math.min(1, t) * Math.PI) * 0.1;
        targetPos.y -= dip;
        targetRotX += dip * 0.7;
      }
    }

    if (this.animState === 'pumping' && def.id === 'shotgun') {
      targetPos.z += this.pumpAmount * 0.03;
      targetPos.y -= this.pumpAmount * 0.018;
      targetRotX -= this.pumpAmount * 0.08;
    }

    // Match camera lean so gun stays natural while Q/E leaning
    targetRotZ += player.lean * -0.12;
    targetPos.x += player.lean * 0.04;

    const poseFollow =
      this.animState === 'switching'
        ? 28
        : this.animState === 'pumping'
          ? 22
          : this.animState === 'reloading' && def.id === 'rpg'
            ? 34
            : this.animState === 'reloading' && (def.id === 'sawedoff' || isMagReloadWeapon(def.id))
              ? 18
              : 16;
    const rotFollow =
      this.animState === 'switching'
        ? 26
        : this.animState === 'pumping'
          ? 24
          : this.animState === 'reloading' && def.id === 'rpg'
            ? 30
            : this.animState === 'reloading' && (def.id === 'sawedoff' || isMagReloadWeapon(def.id))
              ? 20
              : 18;
    this.pose.position.lerp(targetPos, 1 - Math.exp(-poseFollow * dt));
    this.pose.rotation.x = damp(this.pose.rotation.x, targetRotX, rotFollow, dt);
    this.pose.rotation.y = damp(this.pose.rotation.y, targetRotY, poseFollow, dt);
    this.pose.rotation.z = damp(this.pose.rotation.z, targetRotZ, poseFollow, dt);

    // Hard holster offset so the gun actually leaves the frame (lerp alone never gets there).
    const pull = this.switchPull();
    if (pull > 0) {
      this.pose.position.y -= pull * this.switchDrop;
      this.pose.position.z += pull * 0.04;
    }
  }

  /** 0 = on-screen ready, 1 = fully below the view. */
  private switchPull(): number {
    if (this.animState !== 'switching') return 0;
    if (!this.switchSwapped) {
      const u = Math.min(1, this.animT / this.switchDownDur);
      return u * u;
    }
    const u = Math.min(1, (this.animT - this.switchDownDur) / this.switchUpDur);
    const rise = 1 - (1 - u) * (1 - u);
    return 1 - rise;
  }

  ammoString(): string {
    const a = this.ammo[this.weaponId];
    if (this.weaponId === 'punch') return 'MELEE';
    if (this.weaponId === 'shotgun') return `${a.mag} shells`;
    if (this.weaponId === 'gl') return String(a.mag + a.reserve);
    if (this.weaponId === 'sawedoff') return `${a.mag} / ${a.reserve}`;
    return `${a.mag} / ${a.reserve}`;
  }

  /** Compact ammo for the slot strip. */
  hudAmmo(id: WeaponId): string {
    if (id === 'punch') return '—';
    if (!this.owned.has(id)) return '–';
    const a = this.ammo[id];
    if (id === 'shotgun') return String(a.mag);
    return String(a.mag + a.reserve);
  }

  addPistolReserve(n: number): void {
    this.ammo.pistol.reserve += n;
  }

  addShotgunShells(n: number): void {
    this.ammo.shotgun.mag = Math.min(99, this.ammo.shotgun.mag + n);
    this.ammo.sawedoff.reserve += n;
  }

  addReserve(id: WeaponId, n: number): void {
    if (id === 'punch') return;
    if (id === 'shotgun') {
      this.addShotgunShells(n);
      return;
    }
    this.ammo[id].reserve += n;
  }

  /** Grant a findable weapon and fill its mag/reserve. Returns false if already owned. */
  grantWeapon(id: WeaponId): boolean {
    const def = WEAPONS[id];
    const first = !this.owned.has(id);
    this.owned.add(id);
    const a = this.ammo[id];
    if (first) {
      a.mag = def.magSize;
      a.reserve = def.reserveStart;
      // Equip immediately (pickup feedback); skip holster whip mid-combat clutter
      this.pumpAmount = 0;
      this.pendingWeapon = null;
      this.switchTarget = null;
      this.switchSwapped = false;
      this.animState = 'idle';
      this.animT = 0;
      this.weaponId = id;
      this.applyWeaponVisibility();
    } else if (def.requiresPump) {
      a.mag = Math.min(99, a.mag + Math.max(4, Math.floor(def.magSize * 0.5)));
    } else {
      a.reserve += def.magSize;
    }
    return first;
  }

  ownsWeapon(id: WeaponId): boolean {
    return this.owned.has(id);
  }

  /** Live viewmodel orientation (screenshot tuning). */
  debugOrient(
    id: WeaponId,
    rx: number,
    ry: number,
    rz: number,
    scale?: number,
    pos?: [number, number, number],
  ): void {
    const rig = this.rigFor(id);
    if (!rig) return;
    rig.oriented.rotation.order = 'YXZ';
    rig.oriented.rotation.set(rx, ry, rz);
    if (scale != null) rig.oriented.scale.setScalar(scale);
    if (pos) rig.oriented.position.set(pos[0], pos[1], pos[2]);
    rig.root.updateMatrixWorld(true);
  }

  getArms(): ProcArms | null {
    return this.procArms;
  }

  getWeapon(id?: WeaponId): LoadedViewWeapon | null {
    return this.rigFor(id ?? this.weaponId);
  }

  /** Target ADS blend 0..1 (eases at in-game speed). */
  debugSetAds(amount: number): void {
    this.debugAdsLock = Math.max(0, Math.min(1, amount));
  }

  enableCalibFreeze(on: boolean, showFx = true, allowFire = true): void {
    this.calibFrozen = on;
    this.calibAllowFire = on ? allowFire : true;
    if (!on) {
      this.removeCalibFxMarkers();
      return;
    }
    this.recoil = 0;
    this.kickPitch = 0;
    this.kickYaw = 0;
    this.sprintBlend = 0;
    this.sprintArmBlend = 0;
    this.slideLegBlend = 0;
    this.pumpAmount = 0;
    this.animState = 'idle';
    this.animT = 0;
    this.reloadPreview = false;
    this.resetDoubleBarrelIdle();
    this.resetMagReloadIdle();
    this.clearReloadFlyers();
    this.sprintArm.visible = false;
    this.slideLegs.visible = false;
    this.bob.position.set(0, 0, 0);
    this.sway.rotation.set(0, 0, 0);
    if (showFx) this.buildCalibFxMarkers();
  }

  debugEquip(id: WeaponId): void {
    this.owned.add(id);
    const def = WEAPONS[id];
    const a = this.ammo[id];
    a.mag = def.magSize;
    a.reserve = def.reserveStart;
    this.pendingWeapon = null;
    this.switchTarget = null;
    this.switchSwapped = false;
    this.animState = 'idle';
    this.animT = 0;
    this.weaponId = id;
    this.reloadPreview = false;
    this.punchStrike = null;
    this.punchFrozenClip = null;
    this.punchFrozenKey = null;
    this.punchT = 0;
    this.punchPendingHit = false;
    this.resetDoubleBarrelIdle();
    this.resetMagReloadIdle();
    this.clearReloadFlyers();
    this.applyWeaponVisibility();
  }

  get calibWeaponId(): WeaponId {
    return this.weaponId;
  }

  poseFor(id: WeaponId): {
    hipPos: THREE.Vector3;
    hipRot: THREE.Euler;
    adsPos: THREE.Vector3;
    adsRot: THREE.Euler;
  } {
    if (this.procMode) {
      if (id === 'pistol') {
        return {
          hipPos: this.procPistolHipPos,
          hipRot: this.procPistolHipRot,
          adsPos: this.procPistolAdsPos,
          adsRot: this.procPistolAdsRot,
        };
      }
      if (id === 'shotgun') {
        return {
          hipPos: this.procSgHipPos,
          hipRot: this.procSgHipRot,
          adsPos: this.procSgAdsPos,
          adsRot: this.procSgAdsRot,
        };
      }
      if (id === 'sawedoff') {
        return {
          hipPos: this.procSawedHipPos,
          hipRot: this.procSawedHipRot,
          adsPos: this.procSawedAdsPos,
          adsRot: this.procSawedAdsRot,
        };
      }
      if (id === 'mp5') {
        return {
          hipPos: this.procRifleHipPos,
          hipRot: this.procRifleHipRot,
          adsPos: this.procMp5AdsPos,
          adsRot: this.procMp5AdsRot,
        };
      }
      if (id === 'ak47') {
        return {
          hipPos: this.procRifleHipPos,
          hipRot: this.procRifleHipRot,
          adsPos: this.procAk47AdsPos,
          adsRot: this.procRifleAdsRot,
        };
      }
      if (id === 'punch') {
        return {
          hipPos: this.punchHipPos,
          hipRot: this.punchHipRot,
          adsPos: this.punchAdsPos,
          adsRot: this.punchAdsRot,
        };
      }
      if (id === 'gl') {
        return {
          hipPos: this.glHipPos,
          hipRot: this.glHipRot,
          adsPos: this.glAdsPos,
          adsRot: this.glAdsRot,
        };
      }
      if (id === 'rpg') {
        return {
          hipPos: this.rpgHipPos,
          hipRot: this.rpgHipRot,
          adsPos: this.rpgAdsPos,
          adsRot: this.rpgAdsRot,
        };
      }
      return {
        hipPos: this.procHipPos,
        hipRot: this.procHipRot,
        adsPos: this.procAdsPos,
        adsRot: this.procAdsRot,
      };
    }
    if (id === 'shotgun') {
      return {
        hipPos: this.shotgunHipPos,
        hipRot: this.shotgunHipRot,
        adsPos: this.shotgunAdsPos,
        adsRot: this.shotgunAdsRot,
      };
    }
    if (id === 'sawedoff') {
      return {
        hipPos: this.procSawedHipPos,
        hipRot: this.procSawedHipRot,
        adsPos: this.procSawedAdsPos,
        adsRot: this.procSawedAdsRot,
      };
    }
    if (id === 'mp5') {
      return {
        hipPos: this.mp5HipPos,
        hipRot: this.mp5HipRot,
        adsPos: this.mp5AdsPos,
        adsRot: this.mp5AdsRot,
      };
    }
    if (id === 'ak47') {
      return {
        hipPos: this.rifleHipPos,
        hipRot: this.rifleHipRot,
        adsPos: this.rifleAdsPos,
        adsRot: this.rifleAdsRot,
      };
    }
    return {
      hipPos: this.hipPos,
      hipRot: this.hipRot,
      adsPos: this.adsPos,
      adsRot: this.adsRot,
    };
  }

  fxFor(id: WeaponId): {
    muzzleHip: THREE.Vector3;
    muzzleAds: THREE.Vector3;
    casingHip: THREE.Vector3;
    casingAds: THREE.Vector3;
  } | null {
    if (this.procMode) return null;
    if (id === 'mp5') {
      return {
        muzzleHip: this.mp5MuzzleHip,
        muzzleAds: this.mp5MuzzleAds,
        casingHip: this.mp5CasingHip,
        casingAds: this.mp5CasingAds,
      };
    }
    if (id === 'ak47') {
      return {
        muzzleHip: this.rifleMuzzleHip,
        muzzleAds: this.rifleMuzzleAds,
        casingHip: this.rifleCasingHip,
        casingAds: this.rifleCasingAds,
      };
    }
    return null;
  }

  syncCalibFxPreview(): void {
    if (!this.calibMuzzleMarker || !this.calibCasingMarker) return;
    const fx = this.fxFor(this.weaponId);
    if (!fx) {
      this.calibMuzzleMarker.visible = false;
      this.calibCasingMarker.visible = false;
      return;
    }
    const ads = this.ads > 0.5;
    this.calibMuzzleMarker.visible = true;
    this.calibCasingMarker.visible = true;
    this.calibMuzzleMarker.position.copy(ads ? fx.muzzleAds : fx.muzzleHip);
    this.calibCasingMarker.position.copy(ads ? fx.casingAds : fx.casingHip);
  }

  getMeshTune(id: WeaponId): {
    rot: THREE.Euler;
    pos: THREE.Vector3;
    scl: THREE.Vector3;
  } | null {
    const rig = this.rigFor(id);
    if (!rig) return null;
    return {
      rot: rig.oriented.rotation,
      pos: rig.oriented.position,
      scl: rig.oriented.scale,
    };
  }

  private updateCalibFrame(
    player: PlayerController,
    def: WeaponDef,
    input: Input,
    dt: number,
    worldScene: THREE.Object3D,
  ): void {
    this.sprintBlend = 0;
    this.sprintArmBlend = 0;
    this.slideLegBlend = 0;
    this.recoil = 0;
    this.kickPitch = 0;
    this.kickYaw = 0;
    if (this.animState !== 'reloading' && this.animState !== 'pumping') {
      if (!(this.weaponId === 'punch' && (this.punchStrike || this.punchFrozenKey))) {
        this.animState = 'idle';
      }
    }
    this.sprintArm.visible = false;
    this.slideLegs.visible = false;
    this.ads = damp(this.ads, this.debugAdsLock ?? 0, 12, dt);
    player.lookSensitivityScale = 1;
    player.setFovBlend(this.ads, def.adsFov);
    this.fovPunch = 0;
    this.camera.fov = lerp(this.baseFov, def.adsFov - 12, this.ads);
    this.camera.updateProjectionMatrix();
    if (this.weaponId === 'sawedoff' && input.reloadPressed) {
      this.playDoubleReloadPreview();
    }
    if (isMagReloadWeapon(this.weaponId) && input.reloadPressed) {
      this.playMagReloadPreview();
    }
    if (this.weaponId === 'shotgun' && input.reloadPressed) {
      this.playPumpPreview();
    }
    if (this.animState === 'reloading' || this.animState === 'pumping') this.updateAnim(dt);
    if (this.weaponId === 'punch') {
      if (this.punchStrike && !this.punchFrozenKey) {
        this.punchT += dt;
        const dur = punchPlayDuration(this.punchClips[this.punchStrike]);
        if (this.punchT >= dur) {
          this.punchStrike = null;
          this.punchT = 0;
          this.punchPendingHit = false;
          this.animState = 'idle';
        }
      }
      this.applyPunchFists();
    }
    this.updatePose(player, dt);
    this.updateProcMotion(dt);
    this.updateCasings(dt);

    if (this.procMode && this.procArms) {
      this.pose.updateWorldMatrix(true, true);
      const rig = this.activeRig();
      this.procArms.update(
        rig?.gripR ?? null,
        this.reloadLeftGrip() ?? rig?.gripL ?? null,
        0,
        0,
        this.punchAds(),
        this.reloadLeftPose(),
      );
    }

    this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    const mag = this.ammo[def.id];
    mag.mag = def.magSize;
    mag.reserve = def.reserveStart;
    const wantsFire = def.automatic ? input.fireHeld : input.firePressed;
    const overCalibUi = !!document.querySelector('.vm-calib-panel:hover');
    if (this.calibAllowFire && !overCalibUi && wantsFire && this.fireCooldown <= 0) {
      this.tryFire(player, worldScene);
      this.recoil = 0;
      this.kickPitch = 0;
      this.kickYaw = 0;
      this.fovPunch = 0;
    }
    this.updateFlash(dt);
    this.updateSmoke(dt);
    this.updateCasings(dt);
    this.syncCalibFxPreview();
  }

  private buildCalibFxMarkers(): void {
    if (this.calibMuzzleMarker) return;
    const markMat = (color: number) =>
      new THREE.MeshBasicMaterial({
        color,
        depthTest: false,
        transparent: true,
        opacity: 0.88,
      });
    const muzzle = new THREE.Group();
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.012, 12, 12), markMat(0xff6a18));
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.008, 0.038, 8), markMat(0xffcc66));
    spike.rotation.x = -Math.PI / 2;
    spike.position.z = -0.026;
    muzzle.add(ball);
    muzzle.add(spike);
    muzzle.renderOrder = 12;
    this.pose.add(muzzle);
    this.calibMuzzleMarker = muzzle;

    const casing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.01, 0.01, 0.032, 8),
      markMat(0xe8c84a),
    );
    casing.rotation.z = Math.PI / 2;
    casing.renderOrder = 12;
    this.pose.add(casing);
    this.calibCasingMarker = casing;
    this.syncCalibFxPreview();
  }

  private removeCalibFxMarkers(): void {
    if (this.calibMuzzleMarker) {
      this.pose.remove(this.calibMuzzleMarker);
      this.calibMuzzleMarker.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          m.geometry.dispose();
          (m.material as THREE.Material).dispose();
        }
      });
      this.calibMuzzleMarker = null;
    }
    if (this.calibCasingMarker) {
      this.pose.remove(this.calibCasingMarker);
      this.calibCasingMarker.geometry.dispose();
      (this.calibCasingMarker.material as THREE.Material).dispose();
      this.calibCasingMarker = null;
    }
  }

  private buildSprintArm(): void {
    const skin = new THREE.MeshStandardMaterial({
      color: 0xc4a07a,
      roughness: 0.65,
      metalness: 0.05,
    });
    const glove = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.45,
      metalness: 0.15,
    });

    this.sprintArm.add(this.sprintShoulder);
    this.sprintShoulder.position.set(-0.12, -0.28, -0.18);

    const upper = new THREE.Mesh(new THREE.CapsuleGeometry(0.028, 0.11, 4, 8), skin);
    upper.rotation.z = Math.PI / 2;
    upper.position.set(-0.06, 0, 0);
    this.sprintShoulder.add(upper);
    this.sprintShoulder.add(this.sprintElbow);
    this.sprintElbow.position.set(-0.13, 0, 0);

    const fore = new THREE.Mesh(new THREE.CapsuleGeometry(0.024, 0.1, 4, 8), skin);
    fore.rotation.z = Math.PI / 2;
    fore.position.set(-0.055, 0, 0);
    this.sprintElbow.add(fore);
    this.sprintElbow.add(this.sprintWrist);
    this.sprintWrist.position.set(-0.12, 0, 0);

    const palm = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.022, 0.07), glove);
    palm.position.set(-0.02, 0, 0.01);
    this.sprintWrist.add(palm);
    for (let i = 0; i < 4; i++) {
      const finger = new THREE.Mesh(new THREE.CapsuleGeometry(0.006, 0.028, 2, 6), glove);
      finger.rotation.z = Math.PI / 2;
      finger.position.set(-0.055, 0.012 - i * 0.009, 0.02);
      this.sprintWrist.add(finger);
    }
    const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(0.007, 0.022, 2, 6), glove);
    thumb.rotation.set(0.4, 0, Math.PI / 2.4);
    thumb.position.set(-0.02, 0.02, 0.01);
    this.sprintWrist.add(thumb);

    this.sprintArm.visible = false;
  }

  private updateSprintArm(_dt: number): void {
    const a = this.sprintArmBlend * (1 - this.slideLegBlend);
    if (a < 0.01) {
      this.sprintArm.visible = false;
      return;
    }
    this.sprintArm.visible = true;

    // Pump opposite the gun side: forward/back swing + elbow bend
    const swing = Math.sin(this.sprintRunPhase);
    const bend = 0.55 + Math.sin(this.sprintRunPhase + 0.4) * 0.35;

    this.sprintArm.position.set(
      lerp(-0.02, -0.08, a),
      lerp(-0.05, 0.02, a),
      lerp(0.05, 0.0, a),
    );
    this.sprintArm.scale.setScalar(lerp(0.4, 1, a));

    this.sprintShoulder.rotation.set(
      0.15 + swing * 0.55 * a,
      0.35 * a,
      -0.2 - Math.abs(swing) * 0.12 * a,
    );
    this.sprintElbow.rotation.set(0, 0, bend * a);
    this.sprintWrist.rotation.set(swing * 0.2 * a, 0, 0.1 * a);

    // Soft fade via scale already; also tuck when ADS interrupts sprint
    const adsFade = 1 - this.ads;
    this.sprintArm.scale.multiplyScalar(adsFade);
    if (adsFade < 0.05) this.sprintArm.visible = false;
  }

  private buildSlideLegs(): void {
    // Match the third-person agent tuxedo: black wool trousers + oxfords.
    const pant = new THREE.MeshStandardMaterial({
      color: 0x0e0c0a,
      roughness: 0.78,
      metalness: 0.1,
    });
    const stripe = new THREE.MeshStandardMaterial({
      color: 0x1c1a18,
      roughness: 0.42,
      metalness: 0.2,
    });
    const shoe = new THREE.MeshStandardMaterial({
      color: 0x0a0a0a,
      roughness: 0.3,
      metalness: 0.32,
    });

    const makeLeg = (root: THREE.Group, knee: THREE.Group, outer: number) => {
      const thigh = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.1, 0.3), pant);
      thigh.position.set(0, 0, -0.16);
      thigh.frustumCulled = false;
      root.add(thigh);
      const seam = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.012, 0.28), stripe);
      seam.position.set(outer * 0.056, 0.02, -0.16);
      seam.frustumCulled = false;
      root.add(seam);

      knee.position.set(0, 0, -0.31);
      root.add(knee);

      const shin = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.084, 0.26), pant);
      shin.position.set(0, 0, -0.14);
      shin.frustumCulled = false;
      knee.add(shin);
      const shinSeam = new THREE.Mesh(new THREE.BoxGeometry(0.007, 0.01, 0.24), stripe);
      shinSeam.position.set(outer * 0.046, 0.016, -0.14);
      shinSeam.frustumCulled = false;
      knee.add(shinSeam);

      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.086, 0.04, 0.19), shoe);
      foot.position.set(0, -0.028, -0.3);
      foot.frustumCulled = false;
      knee.add(foot);
      const heel = new THREE.Mesh(new THREE.BoxGeometry(0.078, 0.028, 0.05), shoe);
      heel.position.set(0, -0.042, -0.23);
      heel.frustumCulled = false;
      knee.add(heel);
    };

    makeLeg(this.slideLegL, this.slideKneeL, -1);
    makeLeg(this.slideLegR, this.slideKneeR, 1);
    this.slideLegs.add(this.slideLegL);
    this.slideLegs.add(this.slideLegR);
    this.slideLegs.visible = false;
    this.slideLegs.frustumCulled = false;
  }

  private updateSlideLegs(_dt: number): void {
    const a = this.slideLegBlend;
    const show = a > 0.02;
    if (!show) {
      this.slideLegs.visible = false;
      return;
    }
    this.slideLegs.visible = true;
    this.slideLegs.scale.setScalar(lerp(0.4, 1, a));

    if (this.useGlbLegs && this.slideLegsGlb) {
      this.slideLegs.position.set(0.0, -0.42, -0.12);
      this.slideLegsGlb.visible = true;
      this.slideLegL.visible = false;
      this.slideLegR.visible = false;
      return;
    }

    // Negative pitch plants feet on the floor (old +X rotations lifted them into the sky).
    this.slideLegs.position.set(0.0, -0.1, -0.16);
    this.slideLegL.visible = true;
    this.slideLegR.visible = true;
    this.slideLegL.position.set(-0.12, 0.0, -0.02);
    this.slideLegL.rotation.set(-0.38, 0.05, 0.07);
    this.slideKneeL.rotation.set(-0.1, 0, 0);
    this.slideLegR.position.set(0.13, 0.012, 0.1);
    this.slideLegR.rotation.set(-0.48, -0.06, -0.09);
    this.slideKneeR.rotation.set(-0.28, 0, 0);
  }
}

function isIgnoredHitTarget(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.ignoreHit) return true;
    o = o.parent;
  }
  return false;
}

function findDestructibleToilet(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.destructible === 'toilet') return o;
    o = o.parent;
  }
  return null;
}

function findDestructibleLamp(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.destructible === 'lamp') return o;
    o = o.parent;
  }
  return null;
}

function findDestructibleCrate(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.destructible === 'crate') return o;
    o = o.parent;
  }
  return null;
}

function findDestructibleAlarm(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.destructible === 'alarm') return o;
    o = o.parent;
  }
  return null;
}

function findDestructibleGlass(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.destructible === 'glass') return o;
    o = o.parent;
  }
  return null;
}

function findEnemyRoot(obj: THREE.Object3D): THREE.Object3D | null {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (typeof o.userData?.enemyId === 'number') return o;
    o = o.parent;
  }
  return null;
}

/** Flat irregular triangle — reads as broken pane glass. */
function createGlassShardGeometry(large: boolean): THREE.BufferGeometry {
  const s = large ? 0.7 + Math.random() * 0.9 : 0.25 + Math.random() * 0.35;
  const geo = new THREE.BufferGeometry();
  const ax = (0.04 + Math.random() * 0.08) * s;
  const ay = (0.05 + Math.random() * 0.12) * s;
  const bx = (-0.02 - Math.random() * 0.07) * s;
  const by = (0.03 + Math.random() * 0.1) * s;
  const cx = (Math.random() - 0.5) * 0.06 * s;
  const cy = (-0.04 - Math.random() * 0.08) * s;
  const z = (large ? 0.006 : 0.003) * (0.7 + Math.random());
  // Two-sided thin wedge (front + back triangle)
  const positions = new Float32Array([
    ax, ay, z, bx, by, z, cx, cy, z,
    ax, ay, -z, cx, cy, -z, bx, by, -z,
  ]);
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

function destroyCeilingLamp(lamp: THREE.Object3D): void {
  lamp.userData.destroyed = true;
  const light = lamp.userData.lampLight as THREE.PointLight | undefined;
  if (light) light.intensity = 0;
  const tube = lamp.userData.lampTube as THREE.Mesh | undefined;
  if (tube) {
    const mat = tube.material as THREE.MeshStandardMaterial;
    mat.emissiveIntensity = 0;
    mat.color.setHex(0x222428);
    mat.emissive.setHex(0x000000);
  }
  // Drop / shatter look — tip the housing and dim
  lamp.rotation.z += (Math.random() - 0.5) * 0.5;
  lamp.rotation.x += 0.15 + Math.random() * 0.2;
  lamp.position.y -= 0.08;
  lamp.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      if (std.isMeshStandardMaterial && std !== (tube?.material as THREE.Material)) {
        std.color?.offsetHSL?.(0, -0.1, -0.15);
      }
    }
  });
}
